import { notify } from '@/lib/toast';
import { useState, useEffect, useRef, useMemo, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertBanner } from '@/components/ui/alert-banner';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Plus, Shield, Edit, Users, Loader2, Building2, Check, X, Database,
  ArrowRightLeft, RefreshCw, CreditCard, BarChart2, Receipt,
  MapPin, ShoppingCart, Package, PauseCircle, PlayCircle, Zap, Hash, Layers,
  Pencil, Tag, LogIn, AlertTriangle,
  HardDrive, Download, Upload, TableIcon, AlertCircle, CheckCircle2, Trash2,
  Activity, Server, Database as DatabaseIcon, Gauge, Archive, ArchiveRestore, Inbox,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { StatCard } from '@/components/ui/stat-card';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { TabBar } from '@/components/ui/tab-bar';
import { SearchInput } from '@/components/ui/search-input';
import { FilterChips } from '@/components/ui/filter-chips';
import { StatusBadge } from '@/components/ui/status-badge';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { FormDialog } from '@/components/ui/form-dialog';
import { AddonPicker, useDebouncedValue } from '@/components/subscription/AddonPicker';
import { AddonSummary } from '@/components/subscription/AddonSummary';
import { addonLabel } from '@/lib/catalog-labels';
import { diffSelection, isIncludedInPlan, toAddonQuantities, usageWarnings } from '@/lib/addon-basket';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { formatCurrency, formatDate, formatDateOnly } from '@/lib/utils';
import { getTrialState } from '@/lib/trial';
import { GlobalTemplatesTab } from './GlobalTemplatesTab';
import { IncludedFeatures } from '@/components/subscription/IncludedFeatures';
import { PassThroughNote } from '@/components/subscription/PassThroughNote';

interface PermissionGroup {
  module: string;
  permissions: { key: string; label: string }[];
}

interface MigrationJob {
  id: string;
  status: string;
  phase: string | null;
  progress: number;
  errorMsg: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

interface DbStatusInfo {
  tenantId: string;
  tenantSlug: string;
  dbStatus: string;
  hasDedicatedDb: boolean;
  migratedAt: string | null;
  migration: MigrationJob | null;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  allowedPermissions: string | string[];
  dbStatus: string;
  databaseUrl: string | null;
  migratedAt: string | null;
  createdAt: string;
  archivedAt: string | null;
  _count: { users: number };
  subscription?: { status: string; trialEndsAt?: string | null } | null;
  /** T-451 tenant-health aggregates (GET /platform/tenants). */
  testMode: boolean;
  subscriptionStatus: string | null;
  trialEndsAt: string | null;
  overdueAmount: number;
  lastActivityAt: string | null;
  sales30: number;
  usersUsed: number;
  /** null = unlimited */
  usersMax: number | null;
  /** POS-I234: server trial phase; 'expired' = read-only, waiting for a plan */
  trialPhase?: 'live' | 'grace' | 'expired' | null;
  trialExpired?: boolean;
}

type TenantHealthFilter = 'all' | 'trialEnding' | 'waitingPlan' | 'overdue' | 'noActivity';

const NO_ACTIVITY_MS = 14 * 24 * 60 * 60 * 1000;

/** Client-side health filters (T-451). Trial math is owned by getTrialState. */
function matchesHealthFilter(tenant: Tenant, filter: TenantHealthFilter, nowMs: number): boolean {
  switch (filter) {
    case 'trialEnding': {
      const trial = getTrialState(tenant.subscriptionStatus, tenant.trialEndsAt);
      return !!trial && !trial.expired && trial.daysLeft <= 7;
    }
    case 'waitingPlan':
      return tenant.trialExpired === true;
    case 'overdue':
      return tenant.overdueAmount > 0;
    case 'noActivity':
      return !tenant.lastActivityAt || nowMs - new Date(tenant.lastActivityAt).getTime() >= NO_ACTIVITY_MS;
    default:
      return true;
  }
}

const SUBSCRIPTION_STATUSES = ['TRIAL', 'ACTIVE', 'PAST_DUE', 'SUSPENDED', 'CANCELLED'] as const;

interface SubscriptionPlan {
  id: string;
  displayName: string;
  description: string | null;
  priceMonthly: number | null;
  maxUsers: number | null;
  maxProducts: number | null;
  maxLocations: number | null;
  maxPromotions?: number | null;
  maxTables?: number | null;
  includedModules?: string[] | null;
  isActive: boolean;
  sortOrder: number;
  permissionKeys?: string[];
}

interface PlanForm {
  displayName: string;
  description: string;
  priceMonthly: number | string;
  maxUsers: number | string;
  maxProducts: number | string;
  maxLocations: number | string;
  isActive: boolean;
  sortOrder: number;
}

type AddonKind = 'MODULE' | 'QUANTITY';

interface SubscriptionAddon {
  id: string;
  key: string;
  name: string;
  description: string | null;
  priceMonthly: number | null;
  type: AddonKind;
  limitType: string | null;
  limitPerUnit: number | null;
  unit: string | null;
  isActive: boolean;
  permissionKeys?: string[];
  /** T-448 — third-party usage billed by the provider. */
  isPassThrough?: boolean;
  providerName?: string | null;
}

interface AddonForm {
  name: string;
  key: string;
  description: string;
  priceMonthly: number | string;
  type: AddonKind;
  limitType: string;
  limitPerUnit: number | string;
  unit: string;
  isActive: boolean;
  isPassThrough: boolean;
  providerName: string;
}

interface BillLineItem {
  description: string;
  amount: number | string;
}

interface SubscriptionBill {
  id: string;
  billingPeriodStart: string;
  dueDate: string;
  paidAt?: string | null;
  status: string;
  totalAmount: number | string;
  lineItems?: BillLineItem[] | null;
}

interface TenantSubscriptionAddon {
  id: string;
  addonId: string;
  quantity?: number;
  addon?: { name?: string; type?: string; priceMonthly?: number | string | null } | null;
}

interface TenantSubscription {
  planId: string | null;
  status: string;
  plan?: { displayName: string } | null;
  bills?: SubscriptionBill[];
  addons?: TenantSubscriptionAddon[];
  suspendedReason?: string | null;
  suspendedAt?: string | null;
  trialEndsAt?: string | null;
  overrideMaxMobileLinks?: number | null;
  overrideMaxMobileSessions?: number | null;
  overrideMaxMainSessions?: number | null;
}

interface UsageMetric {
  used: number;
  max: number | null;
  pct: number | null;
}

interface SubscriptionUsage {
  users: UsageMetric;
  products: UsageMetric;
  locations: UsageMetric;
  promotions?: UsageMetric;
  tables?: UsageMetric;
  mobileLinks?: UsageMetric;
  mobileSessions?: UsageMetric;
  sales: { last30Days: number; allTime: number };
}

interface SubscriptionUsageData {
  /** POS-I232/I234 trial state of the business (server computed) */
  trialAccess?: boolean;
  trialPhase?: 'live' | 'grace' | 'expired' | null;
  trialExpired?: boolean;
  subscription?: TenantSubscription | null;
  usage?: SubscriptionUsage | null;
}

interface TenantDbStatRow {
  tableName: string;
  label: string;
  tenantRows: number;
  totalRows: number;
  estimatedTenantBytes?: number;
  estimatedTenantSizePretty: string;
}

interface PlatformBackupRecord {
  id: string;
  filename: string;
  fileSizeBytes: number | null;
  status: string;
  triggeredBy: string;
  createdAt: string;
}

interface HealthProbe {
  status: string;
  latencyMs?: number | null;
  error?: string;
}

interface QueueHealthProbe extends HealthProbe {
  backlog?: number;
  waiting?: number;
  active?: number;
  delayed?: number;
  failed?: number;
  completed?: number;
}

interface SystemHealthData {
  overall: string;
  checkedAt: string;
  postgres?: HealthProbe;
  redis?: HealthProbe;
  queue?: QueueHealthProbe;
  prometheus?: HealthProbe;
  fbr?: {
    errorRate?: number;
    failed?: number;
    total?: number;
    byStatus?: Record<string, number>;
  };
}

interface CreateTenantPayload {
  tenantName: string;
  tenantSlug: string;
  adminEmail: string;
  adminPassword: string;
  adminFirstName: string;
  adminLastName: string;
  allowedPermissions?: string[];
}

/** True when a TRIAL's end date has passed. */
function trialExpired(trialEndsAt: string): boolean {
  return getTrialState('TRIAL', trialEndsAt)?.expired ?? false;
}

function DbStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('settings');
  if (status === 'DEDICATED') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2.5 py-0.5 text-xs font-medium text-success-text">
        <Database className="h-3 w-3" />
        {t('dbStatusDedicated')}
      </span>
    );
  }
  if (status === 'MIGRATING') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2.5 py-0.5 text-xs font-medium text-warning-text">
        <Loader2 className="h-3 w-3 animate-spin" />
        {t('dbStatusMigrating')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
      <Database className="h-3 w-3" />
      {t('dbStatusShared')}
    </span>
  );
}

const defaultCreateForm = {
  tenantName: '',
  tenantSlug: '',
  adminEmail: '',
  adminPassword: '',
  adminFirstName: '',
  adminLastName: '',
};

const defaultEditForm = {
  name: '',
  isActive: true,
};

function parseTenantPermissions(raw: string | string[]): string[] {
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function PermissionCheckboxes({
  groups,
  selected,
  onChange,
}: {
  groups: PermissionGroup[];
  selected: string[];
  onChange: (perms: string[]) => void;
}) {
  const { t } = useTranslation('settings');
  const allKeys = groups.flatMap((g) => g.permissions.map((p) => p.key));
  const allSelected = allKeys.length > 0 && allKeys.every((k) => selected.includes(k));

  const isPermSelected = (key: string) => {
    const module = key.split(':')[0];
    return selected.includes(key) || selected.includes(`${module}:*`);
  };

  const togglePerm = (key: string) => {
    if (isPermSelected(key)) {
      // If it was selected via wildcard, expand wildcard and remove this one
      const module = key.split(':')[0];
      if (selected.includes(`${module}:*`)) {
        const group = groups.find((g) => g.module === module);
        const expanded = group
          ? group.permissions.map((p) => p.key).filter((k) => k !== key)
          : [];
        onChange([...selected.filter((s) => s !== `${module}:*`), ...expanded]);
      } else {
        onChange(selected.filter((s) => s !== key));
      }
    } else {
      onChange([...selected, key]);
    }
  };

  const toggleModule = (module: string, permissions: { key: string }[]) => {
    const moduleKeys = permissions.map((p) => p.key);
    const allModuleSelected = moduleKeys.every((k) => isPermSelected(k));
    if (allModuleSelected) {
      onChange(
        selected.filter(
          (s) => !moduleKeys.includes(s) && s !== `${module}:*`,
        ),
      );
    } else {
      const withoutModule = selected.filter(
        (s) => !moduleKeys.includes(s) && s !== `${module}:*`,
      );
      onChange([...withoutModule, ...moduleKeys]);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">
          {t('permissionsSelected', { count: selected.length })}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-xs"
          onClick={() => onChange(allSelected ? [] : [...allKeys])}
        >
          {allSelected ? t('deselectAll') : t('selectAll')}
        </Button>
      </div>

      {groups.map((group) => {
        const moduleKeys = group.permissions.map((p) => p.key);
        const allModuleChecked = moduleKeys.every((k) => isPermSelected(k));
        const someModuleChecked =
          !allModuleChecked && moduleKeys.some((k) => isPermSelected(k));

        return (
          <div
            key={group.module}
            className="rounded-lg border border-border/50 bg-accent/30 p-3"
          >
            <Label className="flex items-center gap-2 cursor-pointer mb-2 font-normal leading-normal text-foreground">
              <Checkbox
                checked={allModuleChecked}
                ref={(el) => {
                  if (el) el.indeterminate = someModuleChecked;
                }}
                onChange={() => toggleModule(group.module, group.permissions)}
              />
              <span className="text-sm font-semibold capitalize">
                {group.module}
              </span>
              <span className="ms-auto text-xs text-muted-foreground">
                {t('allLabel')}
              </span>
            </Label>
            <div className="ms-6 space-y-1">
              {group.permissions.map((perm) => (
                <Label
                  key={perm.key}
                  className="flex items-center gap-2 cursor-pointer hover:bg-accent/50 rounded px-2 py-1 transition-colors font-normal leading-normal text-foreground"
                >
                  <Checkbox
                    checked={isPermSelected(perm.key)}
                    onChange={() => togglePerm(perm.key)}
                  />
                  <span className="text-sm">{perm.label}</span>
                </Label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Plans Tab ──────────────────────────────────────────────────────────────

function PlansTab({ permissionGroups }: { permissionGroups: PermissionGroup[] }) {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();
  const [editPlan, setEditPlan] = useState<SubscriptionPlan | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<Partial<PlanForm>>({});
  const [permKeys, setPermKeys] = useState<string[]>([]);

  const { data: plans = [], isLoading } = useQuery<SubscriptionPlan[]>({
    queryKey: ['subscription-plans-all'],
    queryFn: () => api.get('/platform/subscriptions/plans/all').then((r) => r.data),
  });

  const resetForm = (plan?: SubscriptionPlan) => {
    setForm({
      displayName: plan?.displayName ?? '',
      description: plan?.description ?? '',
      priceMonthly: plan?.priceMonthly ?? 0,
      maxUsers: plan?.maxUsers ?? '',
      maxProducts: plan?.maxProducts ?? '',
      maxLocations: plan?.maxLocations ?? '',
      isActive: plan?.isActive ?? true,
      sortOrder: plan?.sortOrder ?? 0,
    });
    setPermKeys(Array.isArray(plan?.permissionKeys) ? plan.permissionKeys : []);
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        priceMonthly: Number(form.priceMonthly),
        maxUsers: form.maxUsers ? Number(form.maxUsers) : null,
        maxProducts: form.maxProducts ? Number(form.maxProducts) : null,
        maxLocations: form.maxLocations ? Number(form.maxLocations) : null,
        permissionKeys: permKeys,
      };
      return editPlan
        ? api.patch(`/platform/subscriptions/plans/${editPlan.id}`, payload)
        : api.post('/platform/subscriptions/plans', { ...payload, name: form.displayName?.toLowerCase().replace(/[^a-z0-9]+/g, '_') });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-plans-all'] });
      toast.success(editPlan ? t('planUpdated') : t('planCreated'));
      setEditPlan(null);
      setCreateOpen(false);
    },
    onError: (e) => notify.error(e, t('failedToSavePlan')),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{t('subscriptionPlansTitle')}</h2>
          <p className="text-sm text-muted-foreground">{t('subscriptionPlansDesc')}</p>
        </div>
        <Button onClick={() => { resetForm(); setCreateOpen(true); }}>
          <Plus className="me-2 h-4 w-4" /> {t('newPlanBtn')}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {plans.map((plan: SubscriptionPlan) => (
            <Card key={plan.id} className={`transition-all hover:shadow-md ${!plan.isActive ? 'opacity-60' : ''}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-base">{plan.displayName}</p>
                    <p className="text-xs text-muted-foreground">{plan.description}</p>
                  </div>
                  <div className="flex gap-1">
                    {!plan.isActive && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{t('inactiveLabel')}</span>
                    )}
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { resetForm(plan); setEditPlan(plan); }}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="text-2xl font-bold text-primary">
                  {formatCurrency(plan.priceMonthly ?? 0)}
                  <span className="text-sm font-normal text-muted-foreground">{t('perMonthSuffix')}</span>
                </div>
                <IncludedFeatures kind="plan" plan={plan} max={9} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit/Create Plan Dialog */}
      <Dialog
        visible={!!(editPlan || createOpen)}
        onHide={() => { setEditPlan(null); setCreateOpen(false); }}
        header={editPlan ? t('editPlanHeader', { name: editPlan.displayName }) : t('createPlanHeader')}
        style={{ width: '52rem', maxWidth: '100%' }}
        draggable={false}
        resizable={false}
        footer={
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { setEditPlan(null); setCreateOpen(false); }}>{t('cancel')}</Button>
            <Button disabled={saveMutation.isPending || !form.displayName} onClick={() => saveMutation.mutate()}>
              {saveMutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {t('savePlanBtn')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('displayNameLabel')}</Label>
              <Input value={form.displayName ?? ''} onChange={(e) => setForm({ ...form, displayName: e.target.value })} placeholder="Business" />
            </div>
            <div>
              <Label>{t('monthlyPriceLabel')}</Label>
              <Input type="number" value={form.priceMonthly ?? 0} onChange={(e) => setForm({ ...form, priceMonthly: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>{t('descriptionLabel')}</Label>
            <Input value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Plan description" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>{t('maxUsersLabel')}</Label>
              <Input type="number" value={form.maxUsers ?? ''} onChange={(e) => setForm({ ...form, maxUsers: e.target.value })} placeholder="∞" />
            </div>
            <div>
              <Label>{t('maxProductsLabel')}</Label>
              <Input type="number" value={form.maxProducts ?? ''} onChange={(e) => setForm({ ...form, maxProducts: e.target.value })} placeholder="∞" />
            </div>
            <div>
              <Label>{t('maxLocationsLabel')}</Label>
              <Input type="number" value={form.maxLocations ?? ''} onChange={(e) => setForm({ ...form, maxLocations: e.target.value })} placeholder="∞" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="planActive" checked={form.isActive ?? true} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
            <Label htmlFor="planActive" className="cursor-pointer">{t('active')}</Label>
          </div>
          <div className="border-t pt-4">
            <Label className="text-base font-semibold mb-1 block">{t('permissionKeysLabel')}</Label>
            <p className="text-xs text-muted-foreground mb-3">{t('permissionKeysDesc')}</p>
            <PermissionCheckboxes groups={permissionGroups} selected={permKeys} onChange={setPermKeys} />
          </div>
        </div>
      </Dialog>
    </div>
  );
}

// ─── Addons Tab ──────────────────────────────────────────────────────────────

function AddonsTab({ permissionGroups }: { permissionGroups: PermissionGroup[] }) {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();
  const [editAddon, setEditAddon] = useState<SubscriptionAddon | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<Partial<AddonForm>>({});
  const [permKeys, setPermKeys] = useState<string[]>([]);

  const { data: addons = [], isLoading } = useQuery<SubscriptionAddon[]>({
    queryKey: ['subscription-addons-all'],
    queryFn: () => api.get('/platform/subscriptions/addons/all').then((r) => r.data),
  });

  const resetForm = (addon?: SubscriptionAddon) => {
    setForm({
      name: addon?.name ?? '',
      key: addon?.key ?? '',
      description: addon?.description ?? '',
      priceMonthly: addon?.priceMonthly ?? 0,
      type: addon?.type ?? 'MODULE',
      limitType: addon?.limitType ?? '',
      limitPerUnit: addon?.limitPerUnit ?? 1,
      unit: addon?.unit ?? 'month',
      isActive: addon?.isActive ?? true,
      isPassThrough: addon?.isPassThrough ?? false,
      providerName: addon?.providerName ?? '',
    });
    setPermKeys(Array.isArray(addon?.permissionKeys) ? addon.permissionKeys : []);
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        priceMonthly: Number(form.priceMonthly),
        limitPerUnit: form.type === 'QUANTITY' ? Number(form.limitPerUnit) : null,
        limitType: form.type === 'QUANTITY' ? form.limitType : null,
        permissionKeys: form.type === 'MODULE' ? permKeys : [],
        isPassThrough: form.isPassThrough ?? false,
        providerName: form.isPassThrough ? (form.providerName?.trim() || null) : null,
      };
      return editAddon
        ? api.patch(`/platform/subscriptions/addons/${editAddon.id}`, payload)
        : api.post('/platform/subscriptions/addons', { ...payload, key: form.key || form.name?.toLowerCase().replace(/[^a-z0-9]+/g, '_') });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-addons-all'] });
      toast.success(editAddon ? t('addonUpdated') : t('addonCreated'));
      setEditAddon(null);
      setCreateOpen(false);
    },
    onError: (e) => notify.error(e, t('failedToSaveAddon')),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{t('addonsTitle')}</h2>
          <p className="text-sm text-muted-foreground">{t('addonsDesc')}</p>
        </div>
        <Button onClick={() => { resetForm(); setCreateOpen(true); }}>
          <Plus className="me-2 h-4 w-4" /> {t('newAddonBtn')}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {addons.map((addon: SubscriptionAddon) => (
            <Card key={addon.id} className={`transition-all hover:shadow-md ${!addon.isActive ? 'opacity-60' : ''}`}>
              <CardContent className="p-5 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {addon.type === 'MODULE' ? (
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary-text font-medium">
                        <Zap className="h-3 w-3" /> MODULE
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary-text font-medium">
                        <Hash className="h-3 w-3" /> QUANTITY
                      </span>
                    )}
                    {!addon.isActive && <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{t('inactiveLabel')}</span>}
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { resetForm(addon); setEditAddon(addon); }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <p className="font-semibold">{addon.name}</p>
                <p className="text-xs text-muted-foreground">{addon.description}</p>
                <div className="text-lg font-bold text-primary-text">
                  {formatCurrency(addon.priceMonthly ?? 0)}
                  <span className="text-sm font-normal text-muted-foreground">
                    {addon.type === 'QUANTITY' ? ` × qty /${addon.unit}` : t('perMonthSuffix')}
                  </span>
                </div>
                <IncludedFeatures kind="addon" addon={addon} max={4} />
                {addon.isPassThrough && <PassThroughNote providerName={addon.providerName} />}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit/Create Addon Dialog */}
      <Dialog
        visible={!!(editAddon || createOpen)}
        onHide={() => { setEditAddon(null); setCreateOpen(false); }}
        header={editAddon ? t('editAddonHeader', { name: editAddon.name }) : t('createAddonHeader')}
        style={{ width: '48rem', maxWidth: '100%' }}
        draggable={false}
        resizable={false}
        footer={
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { setEditAddon(null); setCreateOpen(false); }}>{t('cancel')}</Button>
            <Button disabled={saveMutation.isPending || !form.name} onClick={() => saveMutation.mutate()}>
              {saveMutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {t('saveAddonBtn')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('addonNameLabel')}</Label>
              <Input value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Extra User" />
            </div>
            <div>
              <Label>{t('monthlyPricePerUnitLabel')}</Label>
              <Input type="number" value={form.priceMonthly ?? 0} onChange={(e) => setForm({ ...form, priceMonthly: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>{t('descriptionLabel')}</Label>
            <Input value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <Label className="mb-1.5 block">{t('addonTypeLabel')}</Label>
            <div className="flex gap-3">
              {(['MODULE', 'QUANTITY'] as const).map((addonType) => (
                <label key={addonType} className={`flex items-center gap-2 rounded-lg border p-3 cursor-pointer flex-1 transition-colors ${form.type === addonType ? 'bg-primary/5 border-primary/40' : 'hover:bg-muted/40'}`}>
                  <input type="radio" name="type" value={addonType} checked={form.type === addonType} onChange={() => setForm({ ...form, type: addonType })} className="accent-primary" />
                  <div>
                    <p className="text-sm font-medium">{addonType}</p>
                    <p className="text-xs text-muted-foreground">
                      {addonType === 'MODULE' ? t('flatMonthlyDesc') : t('perUnitDesc')}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          {form.type === 'QUANTITY' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('limitTypeLabel')}</Label>
                <Dropdown
                  value={form.limitType ?? ''}
                  onChange={(e) => setForm({ ...form, limitType: e.value })}
                  options={[
                    { label: t('selectLimitTypePlaceholder'), value: '' },
                    { label: t('limitTypeUsers'), value: 'max_users' },
                    { label: t('limitTypeLocations'), value: 'max_locations' },
                    { label: t('limitTypeProducts'), value: 'max_products' },
                    { label: t('limitTypePromotions'), value: 'max_promotions' },
                    { label: t('limitTypeTables'), value: 'max_tables' },
                  ]}
                  optionLabel="label"
                  optionValue="value"
                  placeholder={t('selectLimitTypePlaceholder')}
                  className="w-full"
                />
              </div>
              <div>
                <Label>{t('unitsPerQtyLabel')}</Label>
                <Input type="number" min="1" value={form.limitPerUnit ?? 1} onChange={(e) => setForm({ ...form, limitPerUnit: e.target.value })} />
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Checkbox id="addonActive" checked={form.isActive ?? true} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
            <Label htmlFor="addonActive" className="cursor-pointer">{t('active')}</Label>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox id="addonPassThrough" checked={form.isPassThrough ?? false} onChange={(e) => setForm({ ...form, isPassThrough: e.target.checked })} />
              <Label htmlFor="addonPassThrough" className="cursor-pointer">{t('passThroughLabel')}</Label>
            </div>
            {form.isPassThrough && (
              <div>
                <Label htmlFor="addonProviderName">{t('passThroughProviderLabel')}</Label>
                <Input
                  id="addonProviderName"
                  value={form.providerName ?? ''}
                  onChange={(e) => setForm({ ...form, providerName: e.target.value })}
                  placeholder={t('passThroughProviderPlaceholder')}
                />
              </div>
            )}
          </div>
          {form.type === 'MODULE' && (
            <div className="border-t pt-4">
              <Label className="text-base font-semibold mb-1 block">{t('permissionKeysUnlocked')}</Label>
              <p className="text-xs text-muted-foreground mb-3">{t('permissionKeysUnlockedDesc')}</p>
              <PermissionCheckboxes groups={permissionGroups} selected={permKeys} onChange={setPermKeys} />
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}

// ─── Subscription Dialog ──────────────────────────────────────────────────────

function UsageBar({ used, max, pct }: { used: number; max: number | null; pct: number | null }) {
  const color = pct === null ? 'bg-info' : pct >= 90 ? 'bg-destructive' : pct >= 70 ? 'bg-warning' : 'bg-success';
  return (
    <div className="space-y-1">
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: pct !== null ? `${pct}%` : '0%' }} />
      </div>
      <p className="text-xs text-muted-foreground">
        {used.toLocaleString()} used{max !== null ? ` / ${max.toLocaleString()} max` : ' (unlimited)'}
      </p>
    </div>
  );
}

const BILL_STATUS_STYLES: Record<string, string> = {
  PAID: 'bg-success/10 text-success-text',
  PENDING: 'bg-warning/10 text-warning-text',
  OVERDUE: 'bg-destructive/10 text-destructive-text',
  WAIVED: 'bg-muted text-muted-foreground',
};

/** POS-I266: server limits object (maxUsers ... maxMainSessions), null = unlimited. */
type LimitsMap = Record<string, number | null | undefined>;

interface SubscriptionPreviewResult {
  currentBill: number;
  estimatedBill: number;
  currentLimits: LimitsMap | null;
  limits: LimitsMap;
}

/** Usage metrics shown against the limits in the summary: server limit key and the usage field. */
const SUMMARY_USAGE: ReadonlyArray<{ limitKey: string; usageKey: 'users' | 'products' | 'locations' | 'promotions' | 'tables' | 'mobileLinks' | 'mobileSessions' }> = [
  { limitKey: 'maxUsers', usageKey: 'users' },
  { limitKey: 'maxProducts', usageKey: 'products' },
  { limitKey: 'maxLocations', usageKey: 'locations' },
  { limitKey: 'maxPromotions', usageKey: 'promotions' },
  { limitKey: 'maxTables', usageKey: 'tables' },
  { limitKey: 'maxMobileLinks', usageKey: 'mobileLinks' },
  { limitKey: 'maxMobileSessions', usageKey: 'mobileSessions' },
];

function parseOverride(raw: string, min: number): number | null | undefined {
  const s = raw.trim();
  if (s === '') return null;
  const n = Number(s);
  return Number.isInteger(n) && n >= min ? n : undefined; // undefined = invalid
}

function SubscriptionDialog({ tenant, onClose }: { tenant: { id: string; name: string }; onClose: () => void }) {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'plan' | 'usage' | 'billing' | 'suspend'>('plan');
  const [planId, setPlanId] = useState('');
  // draft = the selection being edited; saved = what the business has now (add-on id -> quantity)
  const [draft, setDraft] = useState<Record<string, number>>({});
  const [saved, setSaved] = useState<Record<string, number>>({});
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendConfirmOpen, setSuspendConfirmOpen] = useState(false);
  const [reEnableConfirmOpen, setReEnableConfirmOpen] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [overrides, setOverrides] = useState({ links: '', sessions: '', main: '' });

  const { data: plans = [] } = useQuery<SubscriptionPlan[]>({
    queryKey: ['subscription-plans-all'],
    queryFn: () => api.get('/platform/subscriptions/plans/all').then((r) => r.data),
  });

  const addonsQuery = useQuery<SubscriptionAddon[]>({
    queryKey: ['subscription-addons-all'],
    queryFn: () => api.get('/platform/subscriptions/addons/all').then((r) => r.data),
  });
  const addons = useMemo(() => addonsQuery.data ?? [], [addonsQuery.data]);

  const { data: usageData, isLoading: usageLoading } = useQuery<SubscriptionUsageData>({
    queryKey: ['subscription-usage', tenant.id],
    queryFn: () => api.get(`/platform/subscriptions/tenant/${tenant.id}/usage`).then((r) => r.data),
  });

  const current = usageData?.subscription;
  const usage = usageData?.usage;
  const bills: SubscriptionBill[] = current?.bills ?? [];
  const isSuspended = current?.status === 'SUSPENDED';

  const trialAccess = usageData?.trialAccess === true;
  const trialUnlock = usageData?.trialPhase === 'expired';
  const addonsLoaded = addons.length > 0;
  useEffect(() => {
    if (current) {
      // POS-I234: trial add-ons are not carried over: for a trial business the MODULE selection
      // starts empty (QUANTITY rows keep their quantities). Other businesses: unchanged.
      if (trialAccess && !addonsLoaded) return;
      setPlanId(current.planId ?? '');
      const existing: Record<string, number> = {};
      for (const a of current.addons ?? []) {
        if (trialAccess && addons.find((x: SubscriptionAddon) => x.id === a.addonId)?.type !== 'QUANTITY') continue;
        existing[a.addonId] = a.quantity ?? 1;
      }
      setDraft(existing);
      setSaved(existing);
      setOverrides({
        links: current.overrideMaxMobileLinks != null ? String(current.overrideMaxMobileLinks) : '',
        sessions: current.overrideMaxMobileSessions != null ? String(current.overrideMaxMobileSessions) : '',
        main: current.overrideMaxMainSessions != null ? String(current.overrideMaxMainSessions) : '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, trialAccess, addonsLoaded]);

  const setAddonQty = (addonId: string, qty: number) => {
    setDraft((prev) => {
      const next = { ...prev };
      if (qty <= 0) delete next[addonId];
      else next[addonId] = qty;
      return next;
    });
  };

  const selectedPlan = plans.find((p: SubscriptionPlan) => p.id === planId);
  const activeAddons = useMemo(() => addons.filter((a: SubscriptionAddon) => a.isActive), [addons]);

  // The monthly figures and the new limits come from the server (same addonLineTotal / computeEffectiveLimits as the bill).
  const draftPayload = useMemo(() => toAddonQuantities(draft).sort((a, b) => a.addonId.localeCompare(b.addonId)), [draft]);
  const liveKey = JSON.stringify({ planId, q: draftPayload });
  const previewKey = useDebouncedValue(liveKey, 250);
  const previewQuery = useQuery<SubscriptionPreviewResult>({
    queryKey: ['subscription-preview', tenant.id, previewKey],
    queryFn: () => {
      const k = JSON.parse(previewKey) as { planId: string; q: Array<{ addonId: string; quantity: number }> };
      return api.post(`/platform/subscriptions/tenant/${tenant.id}/preview`, { planId: k.planId, addonQuantities: k.q }).then((r) => r.data);
    },
    enabled: !!planId,
    placeholderData: (prev) => prev,
  });
  const preview = previewQuery.data;
  const calculating = previewKey !== liveKey || previewQuery.isFetching;

  const includedIds = useMemo(
    () => new Set(activeAddons.filter((a: SubscriptionAddon) => !(saved[a.id] > 0) && isIncludedInPlan(a, selectedPlan?.permissionKeys)).map((a: SubscriptionAddon) => a.id)),
    [activeAddons, saved, selectedPlan],
  );

  const changes = useMemo(() => diffSelection(saved, draft), [saved, draft]);
  const nameOf = (id: string) => {
    const a = addons.find((x: SubscriptionAddon) => x.id === id);
    return a ? addonLabel(a.key, a.name) : id;
  };
  const usedByLimit: Record<string, number | undefined> = {};
  if (usage) {
    for (const row of SUMMARY_USAGE) usedByLimit[row.limitKey] = (usage[row.usageKey] as UsageMetric | undefined)?.used;
  }
  const warnings = usageWarnings(preview?.limits, usedByLimit);
  const totalChanges = !!preview && !!current && preview.estimatedBill !== preview.currentBill;
  const dirty = changes.length > 0 || (!!current && !!planId && planId !== (current.planId ?? ''));
  const requestClose = () => { if (dirty && !assignMutation.isPending) setDiscardConfirmOpen(true); else onClose(); };
  const planChanged = !!current && !!planId && planId !== (current.planId ?? '');

  const assignMutation = useMutation({
    mutationFn: () =>
      api.post(`/platform/subscriptions/tenant/${tenant.id}/assign`, {
        planId,
        addonQuantities: toAddonQuantities(draft),
      }).then((r) => r.data),
    onSuccess: (data: { status?: string }) => {
      queryClient.invalidateQueries({ queryKey: ['subscription-usage', tenant.id] });
      queryClient.invalidateQueries({ queryKey: ['subscription-preview', tenant.id] });
      queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
      toast.success(
        trialUnlock && data?.status === 'ACTIVE'
          ? t('planAssignedUnlockedToast')
          : `${t('subscriptionUpdatedToast', { name: tenant.name })}`,
      );
      setSaveConfirmOpen(false);
      onClose();
    },
    onError: () => {
      setSaveConfirmOpen(false);
      toast.error(t('failedToUpdateSubscription'));
    },
  });

  const limitsMutation = useMutation({
    mutationFn: (body: { overrideMaxMobileLinks: number | null; overrideMaxMobileSessions: number | null; overrideMaxMainSessions: number | null }) =>
      api.patch(`/platform/subscriptions/tenant/${tenant.id}/limits`, body).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-usage', tenant.id] });
      queryClient.invalidateQueries({ queryKey: ['subscription-preview', tenant.id] });
      toast.success(t('limitsSavedToast'));
    },
    onError: () => toast.error(t('limitsSaveFailedToast')),
  });

  const saveOverrides = () => {
    const links = parseOverride(overrides.links, 0);
    const sessions = parseOverride(overrides.sessions, 0);
    const main = parseOverride(overrides.main, 1);
    if (links === undefined || sessions === undefined || main === undefined) {
      toast.error(t('limitsInvalidToast'));
      return;
    }
    limitsMutation.mutate({ overrideMaxMobileLinks: links, overrideMaxMobileSessions: sessions, overrideMaxMainSessions: main });
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (tab !== 'plan' || !planId || assignMutation.isPending || calculating) return;
    if (totalChanges) setSaveConfirmOpen(true);
    else assignMutation.mutate();
  };

  const suspendMutation = useMutation({
    mutationFn: () =>
      api.post(`/platform/subscriptions/tenant/${tenant.id}/suspend`, { reason: suspendReason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-usage', tenant.id] });
      queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
      toast.success(`${t('tenantSuspendedToast', { name: tenant.name })}`);
      setSuspendConfirmOpen(false);
      setSuspendReason('');
    },
    onError: () => toast.error(t('failedToSuspend')),
  });

  const reEnableMutation = useMutation({
    mutationFn: () =>
      api.post(`/platform/subscriptions/tenant/${tenant.id}/re-enable`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-usage', tenant.id] });
      queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
      toast.success(`${t('tenantReEnabledToast', { name: tenant.name })}`);
      setReEnableConfirmOpen(false);
    },
    onError: () => toast.error(t('failedToReEnable')),
  });

  const billStatusMutation = useMutation({
    mutationFn: ({ billId, status }: { billId: string; status: string }) =>
      api.patch(`/platform/subscriptions/billing/${billId}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-usage', tenant.id] });
      toast.success(t('billStatusUpdated'));
    },
    onError: () => toast.error(t('failedToUpdateBillStatus')),
  });

  const TABS = [
    { id: 'plan', label: t('planAddonsTab'), icon: CreditCard },
    { id: 'usage', label: t('usageTab'), icon: BarChart2 },
    { id: 'billing', label: t('billingTab'), icon: Receipt },
    { id: 'suspend', label: isSuspended ? t('suspendedTabLabel') : t('suspendEnableTab'), icon: PauseCircle },
  ] as const;

  const billState = bills.some((b) => b.status === 'OVERDUE')
    ? { variant: 'danger' as const, label: t('billingStateOverdue') }
    : bills.some((b) => b.status === 'PENDING')
      ? { variant: 'warning' as const, label: t('billingStateDue') }
      : bills.length > 0
        ? { variant: 'success' as const, label: t('billingStateUpToDate') }
        : null;

  const saveLabel = `${trialUnlock ? t('assignPlanAndUnlock') : t('saveSubscription')}${preview ? ` · ${formatCurrency(preview.estimatedBill)}` : ''}`;
  const planDropdown = (
    <div className="space-y-1.5">
      <Label>{t('planLabel')}</Label>
      <Dropdown
        value={planId}
        onChange={(e) => setPlanId(e.value)}
        options={[
          { label: t('selectPlanPlaceholder'), value: '' },
          ...plans.filter((p: SubscriptionPlan) => p.isActive).map((p: SubscriptionPlan) => ({
            label: `${p.displayName} — ${formatCurrency(p.priceMonthly ?? 0)}${t('perMonthSuffix')}${p.maxUsers ? ` · ${t('planUsersCount', { n: p.maxUsers })}` : ` · ${t('unlimitedUsers')}`}`,
            value: p.id,
          })),
        ]}
        optionLabel="label"
        optionValue="value"
        placeholder={t('selectPlanPlaceholder')}
        className="w-full"
        disabled={assignMutation.isPending}
      />
    </div>
  );

  return (
    <>
      <FormDialog
        open
        size="full"
        title={t('subscriptionDialogTitle', { name: tenant.name })}
        icon={<CreditCard className="h-5 w-5 text-primary-text" />}
        headerExtra={
          <div className="flex flex-wrap items-center gap-2 text-xs font-normal">
            {current?.plan?.displayName && <Badge variant="outline">{current.plan.displayName}</Badge>}
            {current?.status && <StatusBadge status={current.status} />}
            {billState && <Badge variant={billState.variant}>{billState.label}</Badge>}
          </div>
        }
        submitLabel={saveLabel}
        cancelLabel={tab === 'plan' ? t('cancel') : t('addonClose')}
        hideSubmit={tab !== 'plan'}
        submitDisabled={!planId || calculating}
        isPending={assignMutation.isPending}
        onSubmit={handleSubmit}
        onClose={requestClose}
      >
        <TabBar
          bordered={false}
          size="sm"
          className="mb-4 -mt-1"
          tabs={TABS.map((item) => ({
            key: item.id,
            label: item.label,
            icon: item.icon,
            className: item.id === 'suspend' && isSuspended ? 'text-destructive-text' : undefined,
          }))}
          activeTab={tab}
          onTabChange={(key) => setTab(key as typeof tab)}
        />

        {/* ── Tab: Plan & Add-ons (three zones: filters, cards, summary) ── */}
        {tab === 'plan' && (
          <AddonPicker
            addons={activeAddons}
            draft={draft}
            saved={saved}
            mode="replace"
            includedIds={includedIds}
            currentLimits={preview?.currentLimits}
            disabled={assignMutation.isPending || !planId}
            loading={addonsQuery.isLoading}
            error={addonsQuery.isError}
            onRetry={() => addonsQuery.refetch()}
            onChange={setAddonQty}
            railTop={planDropdown}
            notices={
              <>
                {trialUnlock && <AlertBanner tone="warning">{t('trialEndedUnlockInfo')}</AlertBanner>}
                {trialAccess && (
                  <AlertBanner tone="info">
                    {t('trialAddonsNotCarried')}
                    {current?.status === 'TRIAL' && <> {t('assignEndsTrial')}</>}
                  </AlertBanner>
                )}
                {!planId && <AlertBanner tone="info">{t('addonChoosePlanFirst')}</AlertBanner>}
              </>
            }
            summary={
              <AddonSummary
                currentMonthly={preview?.currentBill}
                newMonthly={preview?.estimatedBill}
                calculating={calculating && !!planId}
                previewError={previewQuery.isError}
                changes={changes}
                nameOf={nameOf}
                currentLimits={preview?.currentLimits}
                newLimits={preview?.limits}
                usageRows={SUMMARY_USAGE.filter((r) => usedByLimit[r.limitKey] !== undefined).map((r) => ({ limitKey: r.limitKey, used: usedByLimit[r.limitKey] as number }))}
                warnings={warnings}
              >
                {planChanged && (
                  <p className="border-t pt-3 text-sm">
                    {t('summaryPlanChange', { from: current?.plan?.displayName ?? '—', to: selectedPlan?.displayName ?? '—' })}
                  </p>
                )}
              </AddonSummary>
            }
          />
        )}

        {/* ── Tab: Usage ── */}
        {tab === 'usage' && (
          <div className="space-y-4">
            {usageLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : !usage ? (
              <p className="text-muted-foreground text-sm text-center py-8">{t('noSubscriptionYet')}</p>
            ) : (
              <>
                {current && (
                  <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-4 py-3">
                    <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">{t('currentPlanHeader')}</p>
                      <p className="font-semibold">{current.plan?.displayName}</p>
                    </div>
                    <div className="text-end">
                      <p className="text-xs text-muted-foreground">Status</p>
                      <StatusBadge status={current.status} />
                      {current.status === 'TRIAL' && current.trialEndsAt && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {t(trialExpired(current.trialEndsAt) ? 'trialEndedOn' : 'trialEndsOn', { date: formatDateOnly(current.trialEndsAt) })}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Users className="h-4 w-4 text-info-text" />
                      Users
                    </div>
                    <p className="text-2xl font-bold">{usage.users.used}</p>
                    <UsageBar used={usage.users.used} max={usage.users.max} pct={usage.users.pct} />
                  </div>

                  <div className="rounded-lg border p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Package className="h-4 w-4 text-primary-text" />
                      Products
                    </div>
                    <p className="text-2xl font-bold">{usage.products.used.toLocaleString()}</p>
                    <UsageBar used={usage.products.used} max={usage.products.max} pct={usage.products.pct} />
                  </div>

                  <div className="rounded-lg border p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <MapPin className="h-4 w-4 text-warning-text" />
                      Locations
                    </div>
                    <p className="text-2xl font-bold">{usage.locations.used}</p>
                    <UsageBar used={usage.locations.used} max={usage.locations.max ?? null} pct={usage.locations.pct ?? null} />
                  </div>

                  {usage.promotions !== undefined && (
                    <div className="rounded-lg border p-3 space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Tag className="h-4 w-4 text-primary-text" />
                        Promotion Codes
                      </div>
                      <p className="text-2xl font-bold">{usage.promotions.used}</p>
                      <UsageBar used={usage.promotions.used} max={usage.promotions.max ?? null} pct={usage.promotions.pct ?? null} />
                    </div>
                  )}

                  {usage.tables !== undefined && (
                    <div className="rounded-lg border p-3 space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Layers className="h-4 w-4 text-warning-text" />
                        Tables
                      </div>
                      <p className="text-2xl font-bold">{usage.tables.used}</p>
                      <UsageBar used={usage.tables.used} max={usage.tables.max ?? null} pct={usage.tables.pct ?? null} />
                    </div>
                  )}

                  <div className="rounded-lg border p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <ShoppingCart className="h-4 w-4 text-success-text" />
                      Sales
                    </div>
                    <p className="text-2xl font-bold">{usage.sales.last30Days.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">
                      <p className="text-xs text-muted-foreground">Last 30 days · {usage.sales.allTime.toLocaleString()} all-time</p>
                    </p>
                  </div>
                </div>

                {usage.mobileLinks && usage.mobileSessions && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg border p-3 space-y-2">
                      <p className="text-sm font-medium">{t('addonLimitName_maxMobileLinks')}</p>
                      <p className="text-2xl font-bold">{usage.mobileLinks.used}</p>
                      <UsageBar used={usage.mobileLinks.used} max={usage.mobileLinks.max} pct={usage.mobileLinks.pct} />
                    </div>
                    <div className="rounded-lg border p-3 space-y-2">
                      <p className="text-sm font-medium">{t('addonLimitName_maxMobileSessions')}</p>
                      <p className="text-2xl font-bold">{usage.mobileSessions.used}</p>
                      <UsageBar used={usage.mobileSessions.used} max={usage.mobileSessions.max} pct={usage.mobileSessions.pct} />
                    </div>
                  </div>
                )}

                {current && (
                  <div className="rounded-lg border p-4 space-y-3">
                    <div>
                      <p className="text-sm font-semibold">{t('limitsOverrideTitle')}</p>
                      <p className="text-xs text-muted-foreground">{t('limitsOverrideHelp')}</p>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      {([
                        { id: 'links', label: t('addonLimitName_maxMobileLinks'), limitKey: 'maxMobileLinks', min: 0 },
                        { id: 'sessions', label: t('addonLimitName_maxMobileSessions'), limitKey: 'maxMobileSessions', min: 0 },
                        { id: 'main', label: t('addonLimitName_maxMainSessions'), limitKey: 'maxMainSessions', min: 1 },
                      ] as const).map((f) => {
                        const eff = preview?.currentLimits?.[f.limitKey];
                        return (
                          <div key={f.id} className="space-y-1">
                            <Label htmlFor={'override-' + f.id}>{f.label}</Label>
                            <Input
                              id={'override-' + f.id}
                              type="number"
                              min={f.min}
                              step={1}
                              inputMode="numeric"
                              value={overrides[f.id]}
                              placeholder={t('overridePlaceholder')}
                              onChange={(e) => setOverrides((o) => ({ ...o, [f.id]: e.target.value }))}
                            />
                            {eff !== undefined && (
                              <p className="text-xs text-muted-foreground"><bdi dir="ltr">{t('overrideEffective', { value: eff === null ? t('addonNoLimit') : eff })}</bdi></p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-end">
                      <Button type="button" size="sm" disabled={limitsMutation.isPending} onClick={saveOverrides}>
                        {limitsMutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                        {t('saveLimits')}
                      </Button>
                    </div>
                  </div>
                )}

                {current && current.addons && current.addons.length > 0 && (
                  <div className="rounded-lg border p-3 space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('activeAddons')}</p>
                    <div className="space-y-1">
                      {current.addons.map((a: TenantSubscriptionAddon) => {
                        const isQty = a.addon?.type === 'QUANTITY';
                        const qty = a.quantity ?? 1;
                        const price = Number(a.addon?.priceMonthly ?? 0);
                        const total = isQty ? price * qty : price;
                        return (
                          <div key={a.id} className="flex justify-between text-sm">
                            <span className="flex items-center gap-1.5">
                              {isQty ? <Hash className="h-3 w-3 text-primary-text" /> : <Zap className="h-3 w-3 text-primary-text" />}
                              {a.addon?.name}
                              {isQty && <span className="text-xs text-muted-foreground">×{qty}</span>}
                            </span>
                            <span className="text-muted-foreground">{formatCurrency(total)}/mo</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Tab: Billing History ── */}
        {tab === 'billing' && (
          <div className="space-y-3">
            {usageLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : bills.length === 0 ? (
              <div className="text-center py-10 space-y-2">
                <Receipt className="h-10 w-10 text-muted-foreground/40 mx-auto" />
              <p className="text-muted-foreground text-sm">No bills generated yet.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {bills.map((bill: SubscriptionBill) => (
                  <div key={bill.id} className={`rounded-lg border px-4 py-3 space-y-2 ${bill.status === 'OVERDUE' ? 'border-destructive/30 bg-destructive/10' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">
                          {new Date(bill.billingPeriodStart).toLocaleDateString('en-PK', { month: 'long', year: 'numeric' })}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Due {new Date(bill.dueDate).toLocaleDateString('en-PK')}
                          {bill.paidAt && ` · Paid ${new Date(bill.paidAt).toLocaleDateString('en-PK')}`}
                        </p>
                      </div>
                      <div className="text-end flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${BILL_STATUS_STYLES[bill.status] ?? ''}`}>
                          {bill.status}
                        </span>
                        <span className="font-semibold text-sm">{formatCurrency(Number(bill.totalAmount))}</span>
                      </div>
                    </div>
                    {Array.isArray(bill.lineItems) && bill.lineItems.length > 0 && (
                      <div className="text-xs text-muted-foreground border-t pt-2 space-y-0.5">
                        {bill.lineItems.map((li: BillLineItem, idx: number) => (
                          <div key={idx} className="flex justify-between">
                            <span>{li.description}</span>
                            <span>{formatCurrency(Number(li.amount))}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {bill.status !== 'PAID' && bill.status !== 'WAIVED' && (
                      <div className="flex gap-2 pt-1">
                        <Button type="button"
                          size="sm"
                          variant="outline"
                          className="text-success-text border-success/30 hover:bg-success/10 h-7 text-xs"
                          disabled={billStatusMutation.isPending}
                          onClick={() => billStatusMutation.mutate({ billId: bill.id, status: 'PAID' })}
                        >
                        <Check className="h-3 w-3 me-1" /> {t('markPaid')}
                        </Button>
                        <Button type="button"
                          size="sm"
                          variant="ghost"
                          className="text-muted-foreground h-7 text-xs"
                          disabled={billStatusMutation.isPending}
                          onClick={() => billStatusMutation.mutate({ billId: bill.id, status: 'WAIVED' })}
                        >
                          {t('waive')}
                        </Button>
                      </div>
                    )}
                    {(bill.status === 'PAID' || bill.status === 'WAIVED') && (
                      <Button type="button"
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground h-7 text-xs"
                        disabled={billStatusMutation.isPending}
                        onClick={() => billStatusMutation.mutate({ billId: bill.id, status: 'PENDING' })}
                      >
                        {t('revertToPending')}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Tab: Suspend / Enable ── */}
        {tab === 'suspend' && (
          <div className="space-y-4">
            {isSuspended ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-destructive-text">
                    <PauseCircle className="h-5 w-5" />
                    <p className="font-semibold">{t('tenantCurrentlySuspended')}</p>
                  </div>
                  {current?.suspendedReason && (
                    <p className="text-sm text-destructive-text">{t('suspendedReason', { reason: current.suspendedReason })}</p>
                  )}
                  {current?.suspendedAt && (
                    <p className="text-xs text-destructive-text">
                      {t('suspendedOn', { date: new Date(current.suspendedAt).toLocaleDateString('en-PK', { dateStyle: 'long' }) })}
                    </p>
                  )}
                  <p className="text-sm text-destructive-text">
                    {t('opsBlockedMsg')}
                  </p>
                </div>
                <Button type="button"
                  className="w-full bg-success-solid hover:bg-success-solid/90 text-success-solid-foreground"
                  onClick={() => setReEnableConfirmOpen(true)}
                  disabled={reEnableMutation.isPending}
                >
                  {reEnableMutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                  <PlayCircle className="me-2 h-4 w-4" />
                  {t('reEnableSubscription')}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-lg border border-warning/30 bg-warning/10 p-4">
                  <p className="text-sm text-warning-text font-medium">{t('suspensionImpact')}</p>
                  <ul className="mt-2 space-y-1 text-xs text-warning-text">
                    <li>• Tenant users can still log in</li>
                    <li>• All POST, PATCH, DELETE API calls will return 403</li>
                    <li>• A persistent red banner appears in their dashboard</li>
                    <li>• Read-only access is maintained</li>
                    <li>• Suspension can be reversed at any time</li>
                  </ul>
                </div>
                <div className="space-y-1.5">
                  <Label>{t('reasonForSuspension')}</Label>
                  <Input
                    value={suspendReason}
                    onChange={(e) => setSuspendReason(e.target.value)}
                    placeholder={t('suspendReasonPlaceholder')}
                  />
                </div>
                <Button type="button"
                  variant="destructive"
                  className="w-full"
                  disabled={!suspendReason.trim() || suspendMutation.isPending}
                  onClick={() => setSuspendConfirmOpen(true)}
                >
                  {suspendMutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                  <PauseCircle className="me-2 h-4 w-4" />
                  {t('suspendTenant')}
                </Button>
              </div>
            )}
          </div>
        )}
      </FormDialog>

      {/* Save confirmation: the monthly total changes */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={t('addonSaveConfirmTitle')}
        message={t('addonSaveConfirmMsg', { name: tenant.name, from: formatCurrency(preview?.currentBill ?? 0), to: formatCurrency(preview?.estimatedBill ?? 0) })}
        confirmLabel={t('addonSaveConfirm')}
        variant="warning"
        loading={assignMutation.isPending}
        onConfirm={() => assignMutation.mutate()}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Unsaved draft: confirm before closing */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title={t('addonDiscardTitle')}
        message={t('addonDiscardMsg')}
        confirmLabel={t('addonDiscardConfirm')}
        variant="warning"
        onConfirm={onClose}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      {/* Suspend confirm */}
      <ConfirmDialog
        open={suspendConfirmOpen}
        title={t('suspendTenant')}
        message={t('suspendConfirmMsg', { name: tenant.name })}
        confirmLabel={t('suspendLabel')}
        variant="danger"
        loading={suspendMutation.isPending}
        onConfirm={() => suspendMutation.mutate()}
        onCancel={() => setSuspendConfirmOpen(false)}
      />

      {/* Re-enable confirm */}
      <ConfirmDialog
        open={reEnableConfirmOpen}
        title={t('reEnableTitle')}
        message={t('reEnableConfirmMsg', { name: tenant.name })}
        confirmLabel={t('reEnableLabel')}
        variant="warning"
        loading={reEnableMutation.isPending}
        onConfirm={() => reEnableMutation.mutate()}
        onCancel={() => setReEnableConfirmOpen(false)}
      />
    </>
  );
}

// ─── Tenant Backup & Restore Dialog ─────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

interface TenantBackupDialogProps {
  tenant: Tenant;
  onClose: () => void;
}

function TenantBackupDialog({ tenant, onClose }: TenantBackupDialogProps) {
  const { t } = useTranslation('platform');
  const [tab, setTab] = useState<'stats' | 'backup' | 'restore'>('stats');
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [restoreResult, setRestoreResult] = useState<{ restored: Record<string, number>; skipped: string[] } | null>(null);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: dbStats = [], isLoading: statsLoading } = useQuery<TenantDbStatRow[]>({
    queryKey: ['tenant-db-stats', tenant.id],
    queryFn: () => api.get(`/platform/tenants/${tenant.id}/db-stats`).then((r) => r.data),
    enabled: tab === 'stats',
  });

  const restoreMutation = useMutation({
    mutationFn: async (backupData: unknown) => {
      const { data } = await api.post(`/platform/tenants/${tenant.id}/restore`, backupData);
      return data;
    },
    onSuccess: (data) => {
      setRestoreResult(data);
      toast.success('Restore completed successfully');
      setRestoreFile(null);
    },
    onError: (e: unknown) => {
      notify.error(e, 'Restore failed');
    },
  });

  const handleDownloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const response = await api.get(`/platform/tenants/${tenant.id}/backup`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `pos-backup-${tenant.slug}-${today}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded successfully');
    } catch {
      toast.error('Failed to download backup');
    } finally {
      setDownloadingBackup(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setRestoreFile(file);
    setRestoreResult(null);
  };

  const handleRestore = async () => {
    if (!restoreFile) return;
    try {
      const text = await restoreFile.text();
      const backupData = JSON.parse(text);
      restoreMutation.mutate(backupData);
    } catch {
      toast.error('Invalid backup file — could not parse JSON');
    }
    setRestoreConfirmOpen(false);
  };

  const totalTenantRows = dbStats.reduce((s: number, r: TenantDbStatRow) => s + (r.tenantRows ?? 0), 0);
  const totalEstimatedBytes = dbStats.reduce((s: number, r: TenantDbStatRow) => s + (r.estimatedTenantBytes ?? 0), 0);

  return (
    <>
      <Dialog
        visible={true}
        onHide={onClose}
        header={
          <div className="flex items-center gap-2">
            <HardDrive className="h-5 w-5 text-success-text" />
            <span>Backup & Restore — {tenant.name}</span>
          </div>
        }
        style={{ width: '56rem', maxWidth: '100%' }}
        draggable={false}
        resizable={false}
        footer={
          <div className="flex justify-end">
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        }
      >
        {/* Tabs */}
        <div className="flex border-b mb-4 -mt-1">
          {([
            { id: 'stats', label: 'Database Stats', icon: TableIcon },
            { id: 'backup', label: 'Backup', icon: Download },
            { id: 'restore', label: 'Restore', icon: Upload },
          ] as const).map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-success text-success-text'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Stats Tab ── */}
        {tab === 'stats' && (
          <div className="space-y-4">
            {/* Summary row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border bg-muted/30 p-3 text-center">
                <p className="text-2xl font-bold text-foreground">{totalTenantRows.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Total Rows (this tenant)</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3 text-center">
                <p className="text-2xl font-bold text-foreground">{formatBytes(totalEstimatedBytes)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Est. Tenant Storage</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3 text-center">
                <p className="text-2xl font-bold text-foreground">{dbStats.filter((r: TenantDbStatRow) => r.tenantRows > 0).length}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Non-empty Tables</p>
              </div>
            </div>

            {/* Note about shared DB */}
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 bg-warning/10 border border-warning/30 rounded-lg px-3 py-2">
              <AlertCircle className="h-3.5 w-3.5 text-warning-text shrink-0" />
              This is a shared database. "Est. Tenant Storage" is proportional to row count (tenant rows ÷ all tenant rows × table size).
            </p>

            {statsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="rounded-lg border overflow-hidden">
                <div className="overflow-y-auto max-h-[360px]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
                      <tr>
                        <th className="px-4 py-2.5 text-start text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('colTable')}</th>
                        <th className="px-4 py-2.5 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('colTenantRows')}</th>
                        <th className="px-4 py-2.5 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('colAllRowsEst')}</th>
                        <th className="px-4 py-2.5 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('colEstTenantSize')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dbStats.map((row: TenantDbStatRow) => (
                        <tr key={row.tableName} className={`border-t transition-colors ${row.tenantRows > 0 ? 'hover:bg-accent/40' : 'opacity-40'}`}>
                          <td className="px-4 py-2">
                            <div>
                              <p className="font-medium text-xs">{row.label}</p>
                              <p className="text-[10px] text-muted-foreground font-mono">{row.tableName}</p>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-end">
                            <span className={`font-mono text-xs font-semibold ${row.tenantRows > 0 ? 'text-success-text' : 'text-muted-foreground'}`}>
                              {row.tenantRows.toLocaleString()}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-end font-mono text-xs text-muted-foreground">
                            {row.totalRows.toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-end font-mono text-xs text-muted-foreground">
                            {row.estimatedTenantSizePretty}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Backup Tab ── */}
        {tab === 'backup' && (
          <div className="space-y-4">
            <div className="rounded-lg border border-info/30 bg-info/10 p-4 space-y-2">
              <div className="flex items-center gap-2 text-info-text">
                <Download className="h-4 w-4" />
                <p className="font-semibold text-sm">What's included in the backup</p>
              </div>
              <ul className="text-xs text-info-text space-y-1 ms-6 list-disc">
                <li>All business data: products, sales, customers, inventory, accounting, etc.</li>
                <li>User profiles (names, emails) — passwords are <strong>NOT</strong> included</li>
                <li>Roles and permissions configuration</li>
                <li>Tenant settings (FBR tokens are masked for security)</li>
                <li>All data is filtered to <strong>only this tenant</strong> — no cross-tenant leakage</li>
              </ul>
            </div>

            <div className="rounded-lg border border-warning/30 bg-warning/10 p-3">
              <p className="text-xs text-warning-text">
                <strong>Excluded:</strong> Audit logs, refresh tokens, system migration records, FBR API logs, and customer portal OTPs.
                These are ephemeral and not needed for restore.
              </p>
            </div>

            <div className="flex flex-col items-center gap-4 py-6 rounded-lg border-2 border-dashed border-border bg-muted/20">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-success to-success/80 text-success-foreground shadow-lg">
                <HardDrive className="h-8 w-8" />
              </div>
              <div className="text-center">
                <p className="font-semibold">{tenant.name}</p>
                <p className="text-sm text-muted-foreground">Full tenant data export</p>
              </div>
              <Button
                onClick={handleDownloadBackup}
                disabled={downloadingBackup}
                className="gap-2 bg-success-solid hover:bg-success-solid/90 text-success-solid-foreground"
              >
                {downloadingBackup ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {downloadingBackup ? 'Preparing download...' : 'Download Backup (.json)'}
              </Button>
              <p className="text-xs text-muted-foreground">
                File: pos-backup-{tenant.slug}-{new Date().toISOString().split('T')[0]}.json
              </p>
            </div>
          </div>
        )}

        {/* ── Restore Tab ── */}
        {tab === 'restore' && (
          <div className="space-y-4">
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 space-y-2">
              <div className="flex items-center gap-2 text-destructive-text">
                <AlertCircle className="h-4 w-4" />
                <p className="font-semibold text-sm">⚠️ Restore replaces ALL existing business data</p>
              </div>
              <ul className="text-xs text-destructive-text space-y-1 ms-6 list-disc">
                <li>All existing products, sales, customers, inventory data will be <strong>permanently deleted</strong></li>
                <li>Data from the backup file will be inserted in its place</li>
                <li>User accounts and passwords are <strong>NOT affected</strong> — login still works</li>
                <li>This action <strong>cannot be undone</strong> — download a fresh backup first!</li>
              </ul>
            </div>

            {restoreResult ? (
              <div className="rounded-lg border border-success/30 bg-success/10 p-4 space-y-3">
                <div className="flex items-center gap-2 text-success-text">
                  <CheckCircle2 className="h-5 w-5" />
                  <p className="font-semibold">Restore Completed</p>
                </div>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {Object.entries(restoreResult.restored)
                    .filter(([, count]) => (count as number) > 0)
                    .map(([model, count]) => (
                      <div key={model} className="flex justify-between text-xs bg-success/10 rounded px-2 py-1">
                        <span className="font-mono">{model}</span>
                        <span className="font-semibold text-success-text">{count as number} rows</span>
                      </div>
                    ))}
                </div>
                {restoreResult.skipped.length > 0 && (
                  <p className="text-xs text-warning-text">Skipped: {restoreResult.skipped.join(', ')}</p>
                )}
                <Button variant="outline" size="sm" onClick={() => setRestoreResult(null)} className="w-full">
                  Restore Another Backup
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div
                  className="flex flex-col items-center gap-3 py-8 rounded-lg border-2 border-dashed border-border bg-muted/20 cursor-pointer hover:bg-muted/40 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-10 w-10 text-muted-foreground" />
                  <div className="text-center">
                    <p className="font-medium text-sm">
                      {restoreFile ? restoreFile.name : 'Click to select backup file'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {restoreFile ? `${(restoreFile.size / 1024).toFixed(1)} KB` : 'JSON backup file (.json)'}
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json,application/json"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>

                {restoreFile && (
                  <Button
                    variant="destructive"
                    className="w-full gap-2"
                    disabled={restoreMutation.isPending}
                    onClick={() => setRestoreConfirmOpen(true)}
                  >
                    {restoreMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {restoreMutation.isPending ? 'Restoring... (this may take a while)' : 'Restore from Backup'}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        open={restoreConfirmOpen}
        title="Confirm Restore"
        message={`This will DELETE all business data for "${tenant.name}" and replace it with the backup. User accounts will NOT be affected. This cannot be undone. Proceed?`}
        confirmLabel="Yes, Restore"
        variant="danger"
        loading={restoreMutation.isPending}
        onConfirm={handleRestore}
        onCancel={() => setRestoreConfirmOpen(false)}
      />
    </>
  );
}

// ─── Platform Backups Tab ─────────────────────────────────────────────────────
function PlatformBackupsTab() {
  const { t } = useTranslation('platform');
  const queryClient = useQueryClient();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const { data: stats } = useQuery({
    queryKey: ['backup-stats'],
    queryFn: () => api.get('/platform/backups/stats').then((r) => r.data),
    staleTime: 30_000,
  });

  const { data: backups = [], isLoading } = useQuery<PlatformBackupRecord[]>({
    queryKey: ['platform-backups'],
    queryFn: () => api.get('/platform/backups').then((r) => r.data),
    staleTime: 30_000,
  });

  const triggerMutation = useMutation({
    mutationFn: () => api.post('/platform/backups/trigger').then((r) => r.data),
    onSuccess: () => {
      toast.success('Backup started — check list for status');
      queryClient.invalidateQueries({ queryKey: ['platform-backups'] });
      queryClient.invalidateQueries({ queryKey: ['backup-stats'] });
    },
    onError: (err) => notify.error(err, 'Backup failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/platform/backups/${id}`),
    onSuccess: () => {
      toast.success('Backup deleted');
      queryClient.invalidateQueries({ queryKey: ['platform-backups'] });
      queryClient.invalidateQueries({ queryKey: ['backup-stats'] });
      setConfirmDeleteId(null);
    },
    onError: (err) => notify.error(err, 'Delete failed'),
  });

  const formatBytes = (bytes: number | null) => {
    if (!bytes) return '—';
    if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${Math.round(bytes / 1024)} KB`;
  };

  return (
    <div className="space-y-6">
      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Backups', value: stats?.total ?? '—' },
          { label: 'Successful', value: stats?.successful ?? '—' },
          { label: 'Failed', value: stats?.failed ?? '—' },
          { label: 'Last Backup', value: stats?.lastBackupAt ? new Date(stats.lastBackupAt).toLocaleDateString() : 'Never' },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-muted-foreground">{s.label}</p>
              <p className="text-2xl font-bold mt-1">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-medium">Scheduled Backups</p>
              <p className="text-sm text-muted-foreground">Auto-runs daily at 2:00 AM. Files stored in <code className="text-xs bg-muted px-1 rounded">BACKUP_DIR</code> (default: <code className="text-xs bg-muted px-1 rounded">/backups</code>).</p>
            </div>
            <Button
              size="sm"
              onClick={() => triggerMutation.mutate()}
              disabled={triggerMutation.isPending}
              className="gap-2 shrink-0"
            >
              {triggerMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Run Now
            </Button>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {[1,2,3].map(i => <div key={i} className="h-12 bg-muted animate-pulse rounded" />)}
            </div>
          ) : backups.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No backups yet. Run one manually or wait for the daily schedule.</p>
          ) : (
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  {(['colFilename', 'colSize', 'colStatus', 'colTriggeredBy', 'colDate', ''] as const).map((h) => (
                    <Th key={h}>{h ? t(h) : ''}</Th>
                  ))}
                </Tr>
              </THead>
              <TBody>
                {backups.map((b: PlatformBackupRecord) => (
                  <Tr key={b.id}>
                    <Td label={t('colFilename')} className="text-xs font-mono truncate max-w-[200px]">{b.filename}</Td>
                    <Td label={t('colSize')} className="text-xs">{formatBytes(b.fileSizeBytes)}</Td>
                    <Td label={t('colStatus')}>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        b.status === 'SUCCESS' ? 'bg-success/10 text-success-text' :
                        b.status === 'FAILED' ? 'bg-destructive/10 text-destructive-text' :
                        'bg-warning/10 text-warning-text'
                      }`}>{b.status}</span>
                    </Td>
                    <Td label={t('colTriggeredBy')} className="text-xs capitalize">{b.triggeredBy}</Td>
                    <Td label={t('colDate')} className="text-xs">{new Date(b.createdAt).toLocaleString()}</Td>
                    <Td>
                      <Button variant="ghost" size="sm" className="h-7 text-destructive-text" onClick={() => setConfirmDeleteId(b.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!confirmDeleteId}
        title="Delete Backup"
        message="This will permanently delete the backup file and its record. This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => confirmDeleteId && deleteMutation.mutate(confirmDeleteId)}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  );
}

function HealthPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    ok: 'bg-success/10 text-success-text',
    healthy: 'bg-success/10 text-success-text',
    down: 'bg-destructive/10 text-destructive-text',
    degraded: 'bg-warning/10 text-warning-text',
    not_configured: 'bg-muted text-muted-foreground',
  };
  const label: Record<string, string> = {
    ok: 'Operational', healthy: 'Healthy', down: 'Down', degraded: 'Degraded', not_configured: 'Not configured',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${map[status] ?? 'bg-muted text-muted-foreground'}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${status === 'ok' || status === 'healthy' ? 'bg-success' : status === 'down' ? 'bg-destructive' : status === 'degraded' ? 'bg-warning' : 'bg-muted-foreground'}`} />
      {label[status] ?? status}
    </span>
  );
}

function SystemHealthTab() {
  const { data, isLoading, isFetching, refetch, isError } = useQuery<SystemHealthData>({
    queryKey: ['platform-system-health'],
    queryFn: () => api.get('/platform/system-health').then((r) => r.data),
    refetchInterval: 15_000,
    staleTime: 10_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin me-2" /> Checking system health…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <AlertTriangle className="h-10 w-10 text-warning-text" />
          <p className="font-medium">Could not load system health</p>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 me-2" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const services: { key: string; label: string; icon: typeof Server; probe: HealthProbe | undefined; meta?: string }[] = [
    { key: 'postgres', label: 'PostgreSQL', icon: DatabaseIcon, probe: data.postgres, meta: data.postgres?.latencyMs != null ? `${data.postgres.latencyMs} ms` : undefined },
    { key: 'redis', label: 'Redis', icon: Server, probe: data.redis, meta: data.redis?.latencyMs != null ? `${data.redis.latencyMs} ms` : undefined },
    { key: 'queue', label: 'FBR Queue', icon: Layers, probe: data.queue, meta: data.queue?.status === 'ok' ? `${data.queue.backlog} in backlog` : undefined },
    { key: 'prometheus', label: 'Prometheus', icon: Gauge, probe: data.prometheus, meta: data.prometheus?.latencyMs != null ? `${data.prometheus.latencyMs} ms` : undefined },
  ];

  const fbrErrorTone =
    (data.fbr?.errorRate ?? 0) >= 25 ? 'text-destructive-text' : (data.fbr?.errorRate ?? 0) >= 10 ? 'text-warning-text' : 'text-success-text';

  return (
    <div className="space-y-6">
      {/* Overall banner */}
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-4 py-5">
          <div className="flex items-center gap-3">
            <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${data.overall === 'healthy' ? 'bg-success/15 text-success-text' : 'bg-warning/15 text-warning-text'}`}>
              <Activity className="h-6 w-6" />
            </div>
            <div>
              <p className="text-lg font-bold capitalize">{data.overall}</p>
              <p className="text-xs text-muted-foreground">
                Last checked {new Date(data.checkedAt).toLocaleTimeString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <HealthPill status={data.overall} />
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={`h-4 w-4 me-2 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Service grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {services.map((s) => (
          <Card key={s.key}>
            <CardContent className="py-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <s.icon className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium">{s.label}</span>
                </div>
                <HealthPill status={s.probe?.status ?? 'down'} />
              </div>
              {s.meta && <p className="text-sm text-muted-foreground">{s.meta}</p>}
              {s.probe?.error && <p className="text-xs text-destructive-text break-words">{s.probe.error}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Queue depth + FBR error rate */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="py-5">
            <p className="font-medium mb-4 flex items-center gap-2"><Layers className="h-4 w-4" /> FBR Queue Depth</p>
            {data.queue?.status === 'ok' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { label: 'Waiting', value: data.queue.waiting },
                  { label: 'Active', value: data.queue.active },
                  { label: 'Delayed', value: data.queue.delayed },
                  { label: 'Failed', value: data.queue.failed, tone: (data.queue.failed ?? 0) > 0 ? 'text-destructive-text' : '' },
                  { label: 'Completed', value: data.queue.completed },
                ].map((m) => (
                  <div key={m.label} className="rounded-lg border border-border p-3">
                    <p className="text-xs text-muted-foreground">{m.label}</p>
                    <p className={`text-2xl font-bold mt-1 ${m.tone ?? ''}`}>{m.value ?? 0}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-destructive-text">Queue unreachable</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="py-5">
            <p className="font-medium mb-4 flex items-center gap-2"><Receipt className="h-4 w-4" /> FBR Error Rate (24h)</p>
            <div className="flex items-end gap-6">
              <div>
                <p className={`text-4xl font-bold ${fbrErrorTone}`}>{data.fbr?.errorRate ?? 0}%</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {data.fbr?.failed ?? 0} failed / {data.fbr?.total ?? 0} submissions
                </p>
              </div>
              <div className="flex-1 space-y-1">
                {Object.entries(data.fbr?.byStatus ?? {}).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{status}</span>
                    <span className="font-semibold">{count as number}</span>
                  </div>
                ))}
                {(!data.fbr?.total) && <p className="text-xs text-muted-foreground">No submissions in window</p>}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

interface AddonMatrixData {
  addons: { id: string; key: string; name: string; type: string }[];
  tenants: {
    tenantId: string;
    tenantName: string;
    slug: string;
    hasSubscription: boolean;
    planName: string | null;
    status: string | null;
    addonIds: string[];
  }[];
}

function AddonMatrixTab() {
  const { t } = useTranslation('platform');
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const { data, isLoading, isError, refetch, isFetching } = useQuery<AddonMatrixData>({
    queryKey: ['platform-addon-matrix'],
    queryFn: () => api.get('/platform/subscriptions/addon-matrix').then((r) => r.data),
    staleTime: 15_000,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ tenantId, addonId, active }: { tenantId: string; addonId: string; active: boolean }) =>
      api.patch(`/platform/subscriptions/tenant/${tenantId}/addons/${addonId}/toggle`, { active }).then((r) => r.data),
    onMutate: ({ tenantId, addonId }) => {
      setPending((p) => ({ ...p, [`${tenantId}:${addonId}`]: true }));
    },
    onSuccess: (_res, vars) => {
      toast.success(vars.active ? 'Add-on granted' : 'Add-on revoked');
      queryClient.invalidateQueries({ queryKey: ['platform-addon-matrix'] });
      queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
    },
    onError: (err: unknown) => {
      notify.error(err, 'Failed to update add-on');
    },
    onSettled: (_res, _err, vars) => {
      setPending((p) => {
        const next = { ...p };
        delete next[`${vars.tenantId}:${vars.addonId}`];
        return next;
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin me-2" /> Loading add-on matrix…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <AlertTriangle className="h-10 w-10 text-warning-text" />
          <p className="font-medium">Could not load add-on matrix</p>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 me-2" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const q = search.trim().toLowerCase();
  const tenants = q
    ? data.tenants.filter((t) => t.tenantName.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q))
    : data.tenants;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">
            Grant or revoke add-ons per tenant. Cells are disabled for tenants without an active subscription.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search tenants…" className="w-56" />
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 me-2 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {data.addons.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">No active add-ons defined.</CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <THead>
                <Tr hover={false}>
                  <Th className="sticky start-0 z-10 bg-card min-w-[200px]">{t('colTenant')}</Th>
                  {data.addons.map((a) => (
                    <Th key={a.id} className="px-3 text-center whitespace-nowrap" title={a.key}>
                      <div className="flex flex-col items-center gap-1">
                        <Zap className="h-4 w-4 text-primary-text" />
                        <span className="max-w-[120px] truncate">{a.name}</span>
                      </div>
                    </Th>
                  ))}
                </Tr>
              </THead>
              <TBody>
                {tenants.map((tenant) => (
                  <Tr key={tenant.tenantId}>
                    <Td className="sticky start-0 z-10 bg-card">
                      <div className="font-medium">{tenant.tenantName}</div>
                      <div className="text-xs text-muted-foreground">
                        {tenant.hasSubscription ? (tenant.planName ?? 'Subscribed') : 'No subscription'}
                      </div>
                    </Td>
                    {data.addons.map((addon) => {
                      const active = tenant.addonIds.includes(addon.id);
                      const cellKey = `${tenant.tenantId}:${addon.id}`;
                      const busy = pending[cellKey];
                      const disabled = !tenant.hasSubscription || busy;
                      return (
                        <Td key={addon.id} className="px-3 text-center">
                          <button
                            type="button"
                            disabled={disabled}
                            onClick={() =>
                              toggleMutation.mutate({ tenantId: tenant.tenantId, addonId: addon.id, active: !active })
                            }
                            aria-label={`${active ? 'Revoke' : 'Grant'} ${addon.name} for ${tenant.tenantName}`}
                            title={!tenant.hasSubscription ? 'Assign a plan first' : active ? 'Click to revoke' : 'Click to grant'}
                            className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${
                              disabled && !busy
                                ? 'cursor-not-allowed border-dashed border-border text-muted-foreground/40'
                                : active
                                  ? 'border-success bg-success/15 text-success-text hover:bg-success/25'
                                  : 'border-border text-muted-foreground hover:border-primary/30 hover:text-primary-text'
                            }`}
                          >
                            {busy ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : active ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <X className="h-4 w-4 opacity-40" />
                            )}
                          </button>
                        </Td>
                      );
                    })}
                  </Tr>
                ))}
                {tenants.length === 0 && (
                  <Tr hover={false}>
                    <Td colSpan={data.addons.length + 1} className="py-10 text-center text-muted-foreground">
                      No tenants match “{search}”.
                    </Td>
                  </Tr>
                )}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface BillingRow {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  tenantActive: boolean;
  subscriptionEnabled: boolean;
  plan: string;
  planPrice: number;
  addonTotal: number;
  estimatedBill: number;
  status: string;
  suspendedAt: string | null;
  suspendedReason: string | null;
}

interface PlatformChangeRequest {
  id: string;
  kind: 'ADDON' | 'PLAN';
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  quantity: number;
  note?: string | null;
  reviewNote?: string | null;
  createdAt: string;
  tenant?: { id: string; name: string; slug: string } | null;
  addon?: { name: string; priceMonthly: string | number } | null;
  plan?: { name: string; priceMonthly: string | number } | null;
}

function SubscriptionRequestsTab() {
  const { t } = useTranslation('platform');
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'PENDING' | 'APPROVED' | 'REJECTED' | 'ALL'>('PENDING');
  const [rejectTarget, setRejectTarget] = useState<PlatformChangeRequest | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  const { data = [], isLoading, isError, refetch, isFetching } = useQuery<PlatformChangeRequest[]>({
    queryKey: ['platform-change-requests', statusFilter],
    queryFn: () =>
      api
        .get('/platform/subscriptions/change-requests', { params: statusFilter === 'ALL' ? undefined : { status: statusFilter } })
        .then((r) => r.data),
    staleTime: 20_000,
  });

  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/platform/subscriptions/change-requests/${id}/approve`).then((r) => r.data),
    onSuccess: () => {
      toast.success('Request approved and applied');
      queryClient.invalidateQueries({ queryKey: ['platform-change-requests'] });
    },
    onError: (e) => notify.error(e, 'Could not approve request'),
  });

  const reject = useMutation({
    mutationFn: ({ id, reviewNote }: { id: string; reviewNote?: string }) =>
      api.post(`/platform/subscriptions/change-requests/${id}/reject`, { reviewNote }).then((r) => r.data),
    onSuccess: () => {
      toast.success('Request rejected');
      setRejectTarget(null);
      setRejectNote('');
      queryClient.invalidateQueries({ queryKey: ['platform-change-requests'] });
    },
    onError: (e) => notify.error(e, 'Could not reject request'),
  });

  const statusTone = (s: string) =>
    s === 'APPROVED' ? 'text-success-text bg-success/15'
      : s === 'PENDING' ? 'text-warning-text bg-warning/15'
        : s === 'REJECTED' ? 'text-destructive-text bg-destructive/15'
          : 'text-muted-foreground bg-muted';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {(['PENDING', 'APPROVED', 'REJECTED', 'ALL'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {s.charAt(0) + s.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`h-4 w-4 me-2 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin me-2" /> Loading requests…
        </div>
      ) : isError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <AlertTriangle className="h-10 w-10 text-warning-text" />
            <p className="font-medium">Could not load requests</p>
            <Button variant="outline" onClick={() => refetch()}><RefreshCw className="h-4 w-4 me-2" /> Retry</Button>
          </CardContent>
        </Card>
      ) : data.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
            <Inbox className="h-10 w-10" />
            <p>No {statusFilter === 'ALL' ? '' : statusFilter.toLowerCase()} requests</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>{t('colTenant')}</Th>
                  <Th>{t('colRequest')}</Th>
                  <Th>{t('colNote')}</Th>
                  <Th>{t('colWhen')}</Th>
                  <Th>{t('colStatus')}</Th>
                  <Th />
                </Tr>
              </THead>
              <TBody>
                {data.map((r) => (
                  <Tr key={r.id}>
                    <Td label={t('colTenant')}>
                      <p className="font-medium">{r.tenant?.name ?? '—'}</p>
                      <p className="text-xs text-muted-foreground">{r.tenant?.slug}</p>
                    </Td>
                    <Td label={t('colRequest')}>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-xs px-1.5 py-0.5 rounded bg-primary/15 text-primary-text font-medium">{r.kind}</span>
                        {r.kind === 'ADDON' ? (r.addon?.name ?? 'Add-on') : (r.plan?.name ?? 'Plan')}
                        {r.quantity > 1 && <span className="text-muted-foreground">×{r.quantity}</span>}
                      </span>
                    </Td>
                    <Td label={t('colNote')} className="text-xs text-muted-foreground max-w-[14rem] truncate">{r.note ?? '—'}</Td>
                    <Td label={t('colWhen')} className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(r.createdAt)}</Td>
                    <Td label={t('colStatus')}>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusTone(r.status)}`}>{r.status}</span>
                    </Td>
                    <Td>
                      {r.status === 'PENDING' && (
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" className="h-7 px-2.5 bg-success-solid hover:bg-success-solid/90 text-success-solid-foreground" disabled={approve.isPending} onClick={() => approve.mutate(r.id)}>
                            Approve
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2.5 text-destructive-text" onClick={() => { setRejectTarget(r); setRejectNote(''); }}>
                            Reject
                          </Button>
                        </div>
                      )}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog
        visible={!!rejectTarget}
        onHide={() => setRejectTarget(null)}
        header="Reject request"
        style={{ width: '28rem', maxWidth: '100%' }}
        draggable={false}
        resizable={false}
      >
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Optionally tell the tenant why this request was declined. They'll receive a notification.
          </p>
          <Textarea
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Reason (optional)…"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              disabled={reject.isPending}
              onClick={() => rejectTarget && reject.mutate({ id: rejectTarget.id, reviewNote: rejectNote || undefined })}
            >
              {reject.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reject Request'}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

interface BillingContactForm {
  whatsappNumber: string;
  bankAccountTitle: string;
  bankIban: string;
  bankName: string;
  supportEmail: string;
}

interface BillingContactRecord {
  whatsappNumber: string | null;
  bankAccountTitle: string | null;
  bankIban: string | null;
  bankName: string | null;
  supportEmail: string | null;
}

const EMPTY_BILLING_CONTACT: BillingContactForm = {
  whatsappNumber: '', bankAccountTitle: '', bankIban: '', bankName: '', supportEmail: '',
};

/** T-463 — super-admin card: ClearRing's own receiving details shown to every tenant. */
function BillingContactCard() {
  const { t } = useTranslation('platform');
  const qc = useQueryClient();
  const [form, setForm] = useState<BillingContactForm>(EMPTY_BILLING_CONTACT);

  const { data, isError } = useQuery<BillingContactRecord>({
    queryKey: ['platform-billing-contact'],
    queryFn: () => api.get('/platform/subscriptions/billing-contact').then((r) => r.data),
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      whatsappNumber: data.whatsappNumber ?? '',
      bankAccountTitle: data.bankAccountTitle ?? '',
      bankIban: data.bankIban ?? '',
      bankName: data.bankName ?? '',
      supportEmail: data.supportEmail ?? '',
    });
  }, [data]);

  const save = useMutation({
    mutationFn: (body: BillingContactForm) => api.patch('/platform/subscriptions/billing-contact', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-billing-contact'] });
      toast.success(t('billingContactSaved'));
    },
    onError: (e: unknown) => notify.error(e, t('billingContactSaveFailed')),
  });

  const set = (k: keyof BillingContactForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Card>
      <CardContent className="space-y-4 p-4 sm:p-6">
        <div>
          <h3 className="text-base font-semibold">{t('billingContactTitle')}</h3>
          <p className="text-sm text-muted-foreground">{t('billingContactHelp')}</p>
        </div>
        {isError && <p className="text-sm text-destructive-text">{t('billingContactLoadFailed')}</p>}
        <form
          className="space-y-4"
          onSubmit={(e) => { e.preventDefault(); save.mutate(form); }}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="billing-whatsapp">{t('billingContactWhatsapp')}</Label>
              <Input id="billing-whatsapp" dir="ltr" inputMode="tel" value={form.whatsappNumber} onChange={set('whatsappNumber')} placeholder={t('billingContactWhatsappPh')} />
            </div>
            <div>
              <Label htmlFor="billing-support-email">{t('billingContactSupportEmail')}</Label>
              <Input id="billing-support-email" dir="ltr" type="email" value={form.supportEmail} onChange={set('supportEmail')} placeholder="support@clearring.com" />
            </div>
            <div>
              <Label htmlFor="billing-bank-title">{t('billingContactBankTitle')}</Label>
              <Input id="billing-bank-title" value={form.bankAccountTitle} onChange={set('bankAccountTitle')} />
            </div>
            <div>
              <Label htmlFor="billing-bank-name">{t('billingContactBankName')}</Label>
              <Input id="billing-bank-name" value={form.bankName} onChange={set('bankName')} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="billing-iban">{t('billingContactIban')}</Label>
              <Input id="billing-iban" dir="ltr" value={form.bankIban} onChange={set('bankIban')} placeholder="PK00 XXXX 0000 0000 0000 0000" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Check className="h-4 w-4 me-2" />}
              {t('billingContactSave')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function BillingOverviewTab() {
  return (
    <div className="space-y-5">
      <BillingContactCard />
      <BillingOverviewBody />
    </div>
  );
}

function BillingOverviewBody() {
  const { t } = useTranslation('platform');
  const [search, setSearch] = useState('');
  const { data = [], isLoading, isError, refetch, isFetching } = useQuery<BillingRow[]>({
    queryKey: ['platform-billing-overview'],
    queryFn: () => api.get('/platform/subscriptions/billing').then((r) => r.data),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin me-2" /> Loading billing overview…
      </div>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <AlertTriangle className="h-10 w-10 text-warning-text" />
          <p className="font-medium">Could not load billing overview</p>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 me-2" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const mrr = data.reduce((sum, r) => sum + (r.estimatedBill ?? 0), 0);
  const activeCount = data.filter((r) => r.status === 'ACTIVE').length;
  const suspendedCount = data.filter((r) => r.status === 'SUSPENDED').length;
  const pastDueCount = data.filter((r) => r.status === 'PAST_DUE').length;

  const q = search.trim().toLowerCase();
  const rows = q
    ? data.filter((r) => r.tenantName.toLowerCase().includes(q) || r.tenantSlug.toLowerCase().includes(q) || r.plan.toLowerCase().includes(q))
    : data;

  const statusTone = (s: string) =>
    s === 'ACTIVE' ? 'text-success-text bg-success/15'
      : s === 'PAST_DUE' ? 'text-warning-text bg-warning/15'
        : 'text-destructive-text bg-destructive/15';

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label="Est. monthly revenue" value={mrr} format="currency" icon={BarChart2} />
        <StatCard label="Active" value={activeCount} format="number" icon={Check} accent="success" />
        <StatCard label="Past due" value={pastDueCount} format="number" icon={AlertTriangle} accent="warning" />
        <StatCard label="Suspended" value={suspendedCount} format="number" icon={PauseCircle} accent="danger" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Estimated recurring charges per tenant (plan base + active add-ons).</p>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search tenants / plans…" className="w-56" />
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 me-2 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table mobileCards>
            <THead>
              <Tr hover={false}>
                <Th>{t('colTenant')}</Th>
                <Th>{t('colPlan')}</Th>
                <Th className="text-end">{t('colPlanPrice')}</Th>
                <Th className="text-end">{t('colAddons')}</Th>
                <Th className="text-end">{t('colEstBill')}</Th>
                <Th>{t('colStatus')}</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.map((r) => (
                <Tr key={r.tenantId}>
                  <Td label={t('colTenant')}>
                    <div className="font-medium">{r.tenantName}</div>
                    <div className="text-xs text-muted-foreground font-mono">{r.tenantSlug}</div>
                  </Td>
                  <Td label={t('colPlan')}>{r.plan}</Td>
                  <Td label={t('colPlanPrice')} className="text-end">{formatCurrency(r.planPrice)}</Td>
                  <Td label={t('colAddons')} className="text-end">{formatCurrency(r.addonTotal)}</Td>
                  <Td label={t('colEstBill')} className="text-end font-semibold">{formatCurrency(r.estimatedBill)}</Td>
                  <Td label={t('colStatus')}>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusTone(r.status)}`}>
                      {r.status}
                    </span>
                    {r.suspendedReason && <p className="text-[11px] text-muted-foreground mt-1 max-w-[200px] truncate" title={r.suspendedReason}>{r.suspendedReason}</p>}
                  </Td>
                </Tr>
              ))}
              {rows.length === 0 && (
                <Tr hover={false} className="no-card">
                  <Td colSpan={6} className="py-10 text-center text-muted-foreground">
                    {data.length === 0 ? 'No active subscriptions to bill.' : `No tenants match “${search}”.`}
                  </Td>
                </Tr>
              )}
            </TBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export function PlatformPage() {  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const startImpersonation = useAuthStore((s) => s.startImpersonation);
  const user = useAuthStore((s) => s.user);
  const [search, setSearch] = useState('');
  const [healthFilter, setHealthFilter] = useState<TenantHealthFilter>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  const [dbStatusOpen, setDbStatusOpen] = useState(false);
  const [migrateConfirmOpen, setMigrateConfirmOpen] = useState(false);
  const [customServerEnabled, setCustomServerEnabled] = useState(false);
  const [customServer, setCustomServer] = useState({
    host: '',
    port: '5432',
    user: '',
    password: '',
    dbPrefix: '',
    dbName: '',
  });
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [createForm, setCreateForm] = useState({ ...defaultCreateForm });
  const [createPermissions, setCreatePermissions] = useState<string[]>([]);
  const [editForm, setEditForm] = useState({ ...defaultEditForm });
  const [editPermissions, setEditPermissions] = useState<string[]>([]);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);
  const [toggleConfirm, setToggleConfirm] = useState<{
    open: boolean;
    tenant: Tenant | null;
  }>({ open: false, tenant: null });
  const [showArchived, setShowArchived] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState<{
    open: boolean;
    tenant: Tenant | null;
  }>({ open: false, tenant: null });

  const queryClient = useQueryClient();

  // Impersonation
  const impersonateMutation = useMutation({
    mutationFn: (tenantId: string) => api.post(`/platform/tenants/${tenantId}/impersonate`).then((r) => r.data),
    onSuccess: (data) => {
      startImpersonation(data.user, data.tenant, data.accessToken, data.refreshToken);
      toast.success(t('impersonatingToast', { name: data.tenant.name }));
      navigate('/dashboard');
    },
    onError: () => toast.error(t('failedToImpersonate')),
  });

  // Archive / unarchive
  const archiveMutation = useMutation({
    mutationFn: ({ tenant }: { tenant: Tenant }) =>
      api.post(`/platform/tenants/${tenant.id}/${tenant.archivedAt ? 'unarchive' : 'archive'}`).then((r) => r.data),
    onSuccess: (_data, { tenant }) => {
      toast.success(tenant.archivedAt ? t('tenantUnarchivedToast', 'Tenant restored') : t('tenantArchivedToast', 'Tenant archived'));
      queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
      setArchiveConfirm({ open: false, tenant: null });
    },
    onError: (error: unknown) => {
      notify.error(error, t('failedToArchiveTenant', 'Failed to update tenant'));
    },
  });

  // POS-I185: platform admin exempts a business from the owner e-mail verification (clears the due date)
  const exemptVerifyMutation = useMutation({
    mutationFn: ({ tenant }: { tenant: Tenant }) =>
      api.patch(`/platform/tenants/${tenant.id}`, { emailVerifyExempt: true }).then((r) => r.data),
    onSuccess: () => {
      toast.success(t('emailVerifyExemptToast', 'Exempted from e-mail verification'));
      queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
    },
    onError: (error: unknown) => {
      notify.error(error, t('failedToExemptEmailVerify', 'Failed to update tenant'));
    },
  });

  // Fetch tenants
  const { data: tenants = [], isLoading } = useQuery<Tenant[]>({
    queryKey: ['platform-tenants', showArchived],
    queryFn: async () => {
      const { data } = await api.get('/platform/tenants', {
        params: showArchived ? { includeArchived: 'true' } : undefined,
      });
      return data;
    },
  });

  // Fetch permission groups
  const { data: permissionGroups = [] } = useQuery<PermissionGroup[]>({
    queryKey: ['platform-permissions'],
    queryFn: async () => {
      const { data } = await api.get('/platform/permissions');
      return data;
    },
  });

  // Auto-generate slug
  useEffect(() => {
    if (createOpen && !slugManuallyEdited) {
      setCreateForm((prev) => ({
        ...prev,
        tenantSlug: generateSlug(prev.tenantName),
      }));
    }
  }, [createForm.tenantName, createOpen, slugManuallyEdited]);

  // Reset forms on dialog open
  useEffect(() => {
    if (createOpen) {
      setCreateForm({ ...defaultCreateForm });
      setCreatePermissions([]);
      setSlugManuallyEdited(false);
    }
  }, [createOpen]);

  useEffect(() => {
    if (editOpen && selectedTenant) {
      setEditForm({
        name: selectedTenant.name,
        isActive: selectedTenant.isActive,
      });
    }
  }, [editOpen, selectedTenant]);

  useEffect(() => {
    if (permissionsOpen && selectedTenant) {
      setEditPermissions(parseTenantPermissions(selectedTenant.allowedPermissions));
    }
  }, [permissionsOpen, selectedTenant]);

  // Create tenant mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      const payload: CreateTenantPayload = {
        tenantName: createForm.tenantName,
        tenantSlug: createForm.tenantSlug,
        adminEmail: createForm.adminEmail,
        adminPassword: createForm.adminPassword,
        adminFirstName: createForm.adminFirstName,
        adminLastName: createForm.adminLastName,
      };
      if (createPermissions.length > 0) {
        payload.allowedPermissions = createPermissions;
      }
      const { data } = await api.post('/platform/tenants', payload);
      return data;
    },
    onSuccess: () => {
      toast.success(t('tenantCreated'));
      queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
      setCreateOpen(false);
    },
    onError: (error: unknown) => {
      notify.error(error, t('failedToCreateTenant'));
    },
  });

  // Edit tenant mutation
  const editMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTenant) return;
      const { data } = await api.patch(`/platform/tenants/${selectedTenant.id}`, {
        name: editForm.name,
        isActive: editForm.isActive,
      });
      return data;
    },
    onSuccess: () => {
      toast.success(t('tenantUpdated'));
      queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
      setEditOpen(false);
    },
    onError: (error: unknown) => {
      notify.error(error, t('failedToUpdateTenant'));
    },
  });

  // Update permissions mutation
  const permissionsMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTenant) return;
      const { data } = await api.patch(
        `/platform/tenants/${selectedTenant.id}/permissions`,
        { allowedPermissions: editPermissions },
      );
      return data;
    },
    onSuccess: () => {
      toast.success(t('permissionsUpdatedMsg'));
      queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
      setPermissionsOpen(false);
    },
    onError: (error: unknown) => {
      notify.error(error, t('failedToUpdatePermissions'));
    },
  });

  // Toggle active mutation
  const toggleMutation = useMutation({
    mutationFn: async (tenant: Tenant) => {
      const { data } = await api.patch(`/platform/tenants/${tenant.id}`, {
        isActive: !tenant.isActive,
      });
      return data;
    },
    onSuccess: () => {
      toast.success(t('tenantStatusUpdated'));
      queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
    },
    onError: (error: unknown) => {
      notify.error(error, t('failedToUpdateTenantStatus'));
    },
  });

  // DB status query (polling when dialog open + tenant is MIGRATING)
  const { data: dbStatus } = useQuery<DbStatusInfo>({
    queryKey: ['tenant-db-status', selectedTenant?.id],
    queryFn: async () => {
      const { data } = await api.get(`/platform/tenants/${selectedTenant!.id}/db-status`);
      return data;
    },
    enabled: dbStatusOpen && !!selectedTenant,
    refetchInterval: (query) => {
      const status = query.state.data?.migration?.status;
      return status === 'RUNNING' || status === 'PROVISIONING' || status === 'COPYING' ||
             status === 'VERIFYING' || status === 'SWITCHING' || status === 'PENDING'
        ? 2000
        : false;
    },
  });

  // Migrate to dedicated mutation
  const migrateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTenant) return;
      const body: Record<string, string> = {};
      if (customServerEnabled && customServer.host && customServer.user) {
        const auth = `${encodeURIComponent(customServer.user)}:${encodeURIComponent(customServer.password)}`;
        const hostPort = `${customServer.host}:${customServer.port || '5432'}`;
        body.customAdminUrl = `postgresql://${auth}@${hostPort}/postgres`;
        body.customBaseUrl = `postgresql://${auth}@${hostPort}`;
        if (customServer.dbPrefix) body.customDbPrefix = customServer.dbPrefix;
        if (customServer.dbName) body.customDbName = customServer.dbName;
      }
      const { data } = await api.post(`/platform/tenants/${selectedTenant.id}/migrate-to-dedicated`, body);
      return data;
    },
    onSuccess: () => {
      toast.success(t('migrationStartedToast'));
      queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
      setMigrateConfirmOpen(false);
      setDbStatusOpen(true);
    },
    onError: (error: unknown) => {
      notify.error(error, t('failedToStartMigration'));
    },
  });

  // Filtered tenants
  const nowMs = Date.now();
  const searched = tenants.filter(
    (t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.slug.toLowerCase().includes(search.toLowerCase()),
  );
  const filtered = searched.filter((tn) => matchesHealthFilter(tn, healthFilter, nowMs));
  const healthCount = (f: TenantHealthFilter) => searched.filter((tn) => matchesHealthFilter(tn, f, nowMs)).length;

  // Stats
  const totalTenants = tenants.length;
  const activeTenants = tenants.filter((t) => t.isActive).length;
  const inactiveTenants = totalTenants - activeTenants;

  const isCreateValid =
    createForm.tenantName.trim() &&
    createForm.tenantSlug.trim() &&
    /^[a-z0-9-]+$/.test(createForm.tenantSlug) &&
    createForm.adminEmail.trim() &&
    createForm.adminPassword.length >= 8 &&
    createForm.adminFirstName.trim() &&
    createForm.adminLastName.trim();

  const [pageTab, setPageTab] = useState<'tenants' | 'plans' | 'addons' | 'matrix' | 'requests' | 'bills' | 'templates' | 'backups' | 'health'>('tenants');

  if (!user?.isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 text-center p-8">
        <AlertTriangle className="h-12 w-12 text-muted-foreground" />
        <h2 className="text-xl font-semibold">{t('accessDenied')}</h2>
        <p className="text-muted-foreground">{t('accessDeniedMsg')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title={t('platformTitle')}
        subtitle={t('platformSubtitle')}
        actions={pageTab === 'tenants' && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="me-2 h-4 w-4" />
            {t('createTenant')}
          </Button>
        )}
      />

      <TabBar
        className="mb-6"
        tabs={[
          { key: 'tenants', label: t('tenantsTab'), icon: Building2 },
          { key: 'plans', label: t('subscriptionPlansTab'), icon: Layers },
          { key: 'addons', label: t('addonsTabLabel'), icon: Zap },
          { key: 'matrix', label: 'Addon Matrix', icon: TableIcon },
          { key: 'requests', label: 'Requests', icon: Inbox },
          { key: 'bills', label: 'Bills', icon: Receipt },
          { key: 'templates', label: 'Global Templates', icon: Package },
          { key: 'backups', label: 'DB Backups', icon: HardDrive },
          { key: 'health', label: 'System Health', icon: Activity },
        ]}
        activeTab={pageTab}
        onTabChange={(key) => setPageTab(key as typeof pageTab)}
      />

      {/* ── Tenants Tab ── */}
      {pageTab === 'tenants' && (<>
      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
        <StatCard label={t('totalTenants')} value={totalTenants} format="number" icon={Building2} />
        <StatCard label={t('activeTenants')} value={activeTenants} format="number" icon={Check} accent="success" />
        <StatCard label={t('inactiveTenants')} value={inactiveTenants} format="number" icon={X} accent="danger" />
      </div>

      {/* Search */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('searchTenantsPlaceholder')}
          className="max-w-sm w-full"
        />
        <Label className="inline-flex items-center gap-2 font-normal leading-normal text-muted-foreground cursor-pointer select-none">
          <Checkbox
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          {t('showArchivedTenants', 'Show archived')}
        </Label>
      </div>

      {/* T-451 health filters (status filters → FilterChips, not tabs) */}
      <FilterChips
        className="mb-4"
        size="sm"
        variant="outline"
        value={healthFilter}
        onChange={(key) => setHealthFilter(key as TenantHealthFilter)}
        chips={[
          { key: 'all', label: t('tenantFilterAll'), count: searched.length },
          { key: 'trialEnding', label: t('tenantFilterTrialEnding'), count: healthCount('trialEnding') },
          { key: 'waitingPlan', label: t('tenantFilterWaitingPlan'), count: healthCount('waitingPlan') },
          { key: 'overdue', label: t('tenantFilterOverdue'), count: healthCount('overdue') },
          { key: 'noActivity', label: t('tenantFilterNoActivity'), count: healthCount('noActivity') },
        ]}
      />

      {/* Tenant Table */}
      <Card className="animate-fade-in-up">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
          <Table mobileCards>
            <THead>
              <Tr hover={false}>
                <Th className="px-3">{t('colTenantName')}</Th>
                <Th className="px-3">{t('colStatus')}</Th>
                <Th className="px-3">{t('colSubscription')}</Th>
                <Th className="px-3">{t('colLastActivity')}</Th>
                <Th className="hidden px-3 xl:table-cell">{t('colDatabase')}</Th>
                <Th className="hidden px-3 xl:table-cell">{t('colCreatedAt')}</Th>
                <Th className="px-3 text-end">{t('colActions')}</Th>
              </Tr>
            </THead>
            <TBody>
              {isLoading ? (
                <Tr hover={false} className="no-card">
                  <Td colSpan={7} className="py-8 text-center text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin inline-block me-2" />
                    {t('loadingTenants')}
                  </Td>
                </Tr>
              ) : filtered.length === 0 ? (
                <Tr hover={false} className="no-card">
                  <Td colSpan={7} className="py-8 text-center text-muted-foreground">
                    {t('noTenantsFound')}
                  </Td>
                </Tr>
              ) : (
                filtered.map((tenant) => {
                  const trial = getTrialState(tenant.subscriptionStatus, tenant.trialEndsAt);
                  return (
                    <Tr
                      key={tenant.id}
                      className="duration-150"
                    >
                      <Td label={t('colTenantName')} className="px-3 font-medium">
                        <div className="flex flex-col items-start gap-0.5">
                          <span>{tenant.name}</span>
                          <span className="font-mono text-xs font-normal text-muted-foreground">{tenant.slug}</span>
                          <span
                            className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-normal text-muted-foreground"
                            title={t('colUsers')}
                          >
                            <Users className="h-3.5 w-3.5 shrink-0" />
                            <bdi>{tenant.usersUsed} / {tenant.usersMax ?? '∞'}</bdi>
                          </span>
                        </div>
                      </Td>
                      <Td label={t('colStatus')} className="px-3">
                        <div className="flex flex-col items-start gap-1 whitespace-nowrap">
                          <StatusBadge status={tenant.isActive ? 'ACTIVE' : 'INACTIVE'} />
                          {tenant.archivedAt && (
                            <span className="inline-flex items-center rounded-full bg-warning/10 px-2 py-0.5 text-[11px] font-medium text-warning-text">
                              {t('archivedBadge', 'Archived')}
                            </span>
                          )}
                          {tenant.testMode && (
                            <StatusBadge status="STAGING" label={t('testModeBadge')} />
                          )}
                        </div>
                      </Td>
                      <Td label={t('colSubscription')} className="px-3">
                        <div className="flex flex-col items-start gap-1">
                          {tenant.subscriptionStatus ? (
                            <>
                              <StatusBadge
                                status={tenant.subscriptionStatus}
                                label={(SUBSCRIPTION_STATUSES as readonly string[]).includes(tenant.subscriptionStatus)
                                  ? t(`subStatus_${tenant.subscriptionStatus}`)
                                  : undefined}
                              />
                              {tenant.trialPhase === 'expired' && (
                                <StatusBadge status="SUSPENDED" tone="danger" label={t('trialChipViewOnly')} />
                              )}
                              {tenant.trialPhase === 'grace' && (
                                <StatusBadge status="TRIAL" tone="warning" label={t('trialChipGrace')} />
                              )}
                              {tenant.trialPhase === 'expired' && tenant.trialEndsAt && (
                                <span className="text-xs text-muted-foreground">
                                  {t('trialEndedOn', { date: formatDateOnly(tenant.trialEndsAt) })}
                                </span>
                              )}
                              {trial && tenant.trialPhase !== 'expired' && (
                                <span className="text-xs text-muted-foreground">
                                  {trial.expired
                                    ? t('trialEndedOn', { date: formatDateOnly(tenant.trialEndsAt as string) })
                                    : trial.daysLeft === 0
                                      ? t('trialBadgeEndsToday')
                                      : t('trialDaysLeftShort', { count: trial.daysLeft })}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                          {tenant.overdueAmount > 0 && (
                            <span className="text-xs font-medium text-destructive-text">
                              {t('colOverdue')}: <bdi>{formatCurrency(tenant.overdueAmount)}</bdi>
                            </span>
                          )}
                        </div>
                      </Td>
                      <Td label={t('colLastActivity')} className="px-3">
                        <div className="flex flex-col gap-0.5 whitespace-nowrap">
                          <span className={tenant.lastActivityAt ? undefined : 'text-muted-foreground'}>
                            {tenant.lastActivityAt ? formatDateOnly(tenant.lastActivityAt) : '—'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {t('sales30Count', { count: tenant.sales30 })}
                          </span>
                        </div>
                      </Td>
                      <Td label={t('colDatabase')} className="hidden px-3 xl:table-cell">
                        <DbStatusBadge status={tenant.dbStatus || 'SHARED'} />
                      </Td>
                      <Td label={t('colCreatedAt')} className="hidden whitespace-nowrap px-3 text-muted-foreground xl:table-cell">
                        {formatDateOnly(tenant.createdAt)}
                      </Td>
                      <Td label={t('colActions')} className="px-3 text-end">
                        <TableRowActions
                          actions={[
                            {
                              key: 'impersonate',
                              label: 'Impersonate tenant',
                              icon: impersonateMutation.isPending && impersonateMutation.variables === tenant.id ? Loader2 : LogIn,
                              primary: true,
                              disabled: impersonateMutation.isPending,
                              onSelect: () => impersonateMutation.mutate(tenant.id),
                            },
                            {
                              key: 'edit',
                              label: 'Edit tenant',
                              icon: Edit,
                              primary: true,
                              onSelect: () => {
                                setSelectedTenant(tenant);
                                setEditOpen(true);
                              },
                            },
                            {
                              key: 'permissions',
                              label: 'Manage permissions',
                              icon: Shield,
                              onSelect: () => {
                                setSelectedTenant(tenant);
                                setPermissionsOpen(true);
                              },
                            },
                            {
                              key: 'subscription',
                              label: 'Manage subscription',
                              icon: CreditCard,
                              onSelect: () => {
                                setSelectedTenant(tenant);
                                setSubscriptionOpen(true);
                              },
                            },
                            {
                              key: 'database',
                              label: 'Database management',
                              icon: Database,
                              onSelect: () => {
                                setSelectedTenant(tenant);
                                setDbStatusOpen(true);
                              },
                            },
                            {
                              key: 'backup',
                              label: 'Backup & Restore',
                              icon: HardDrive,
                              onSelect: () => {
                                setSelectedTenant(tenant);
                                setBackupOpen(true);
                              },
                            },
                            {
                              key: 'exempt-email-verify',
                              label: t('exemptEmailVerify', 'Exempt from e-mail verification'),
                              icon: Check,
                              disabled: exemptVerifyMutation.isPending,
                              onSelect: () => exemptVerifyMutation.mutate({ tenant }),
                            },
                            {
                              key: 'archive',
                              label: tenant.archivedAt ? t('unarchiveTenant', 'Restore tenant') : t('archiveTenant', 'Archive tenant'),
                              icon: tenant.archivedAt ? ArchiveRestore : Archive,
                              disabled: archiveMutation.isPending,
                              onSelect: () => setArchiveConfirm({ open: true, tenant }),
                            },
                            {
                              key: 'toggle-active',
                              label: tenant.isActive ? 'Deactivate' : 'Activate',
                              icon: tenant.isActive ? X : Check,
                              variant: tenant.isActive ? 'destructive' : 'default',
                              onSelect: () => setToggleConfirm({ open: true, tenant }),
                            },
                          ]}
                        />
                      </Td>
                    </Tr>
                  );
                })
              )}
            </TBody>
          </Table>
          </div>
        </CardContent>
      </Card>
      </>)}

      {/* ── Plans Tab ── */}
      {pageTab === 'plans' && (
        <PlansTab permissionGroups={permissionGroups} />
      )}

      {/* ── Addons Tab ── */}
      {pageTab === 'addons' && (
        <AddonsTab permissionGroups={permissionGroups} />
      )}

      {/* ── Addon Matrix Tab ── */}
      {pageTab === 'matrix' && (
        <AddonMatrixTab />
      )}

      {/* ── Subscription Requests Tab ── */}
      {pageTab === 'requests' && (
        <SubscriptionRequestsTab />
      )}

      {/* ── Bills Tab ── */}
      {pageTab === 'bills' && (
        <BillingOverviewTab />
      )}

      {/* ── DB Backups Tab ── */}
      {pageTab === 'backups' && (
        <PlatformBackupsTab />
      )}

      {/* ── Global Templates Tab ── */}
      {pageTab === 'templates' && (
        <GlobalTemplatesTab />
      )}

      {/* ── System Health Tab ── */}
      {pageTab === 'health' && (
        <SystemHealthTab />
      )}

      {/* ── Backup & Restore Dialog ── */}
      {backupOpen && selectedTenant && (
        <TenantBackupDialog
          tenant={selectedTenant}
          onClose={() => { setBackupOpen(false); setSelectedTenant(null); }}
        />
      )}

      {/* Create Tenant Dialog */}
      <Dialog
        visible={createOpen}
        onHide={() => setCreateOpen(false)}
        header={t('createTenantHeader')}
        footer={
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setCreateOpen(false)}>
              {t('cancel')}
            </Button>
            <Button
              className="flex-1"
              disabled={createMutation.isPending || !isCreateValid}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending && (
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
              )}
              {t('createTenant')}
            </Button>
          </div>
        }
        style={{ width: '42rem', maxWidth: '100%' }}
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4">
          <div>
            <Label>{t('businessNameRequired')}</Label>
            <Input
              value={createForm.tenantName}
              onChange={(e) =>
                setCreateForm({ ...createForm, tenantName: e.target.value })
              }
              placeholder="Acme Coffee Shop"
            />
          </div>

          <div>
            <Label>{t('businessSlugRequired')}</Label>
            <Input
              value={createForm.tenantSlug}
              onChange={(e) => {
                setSlugManuallyEdited(true);
                setCreateForm({ ...createForm, tenantSlug: e.target.value });
              }}
              placeholder="acme-coffee-shop"
              className="font-mono text-sm"
            />
            {createForm.tenantSlug &&
              !/^[a-z0-9-]+$/.test(createForm.tenantSlug) && (
                <p className="text-xs text-destructive-text mt-1">
                  {t('slugInvalidError')}
                </p>
              )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('adminFirstNameRequired')}</Label>
              <Input
                value={createForm.adminFirstName}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    adminFirstName: e.target.value,
                  })
                }
                placeholder="John"
              />
            </div>
            <div>
              <Label>{t('adminLastNameRequired')}</Label>
              <Input
                value={createForm.adminLastName}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    adminLastName: e.target.value,
                  })
                }
                placeholder="Doe"
              />
            </div>
          </div>

          <div>
            <Label>{t('adminEmailRequired')}</Label>
            <Input
              type="email"
              value={createForm.adminEmail}
              onChange={(e) =>
                setCreateForm({ ...createForm, adminEmail: e.target.value })
              }
              placeholder="admin@acme.com"
            />
          </div>

          <div>
            <Label>{t('adminPasswordRequired')}</Label>
            <Input
              type="password"
              value={createForm.adminPassword}
              onChange={(e) =>
                setCreateForm({
                  ...createForm,
                  adminPassword: e.target.value,
                })
              }
              placeholder="Minimum 8 characters"
            />
            {createForm.adminPassword &&
              createForm.adminPassword.length < 8 && (
                <p className="text-xs text-destructive-text mt-1">
                  Password must be at least 8 characters
                </p>
              )}
          </div>

          {/* Permissions */}
          <div className="border-t border-border/50 pt-4">
            <Label className="text-base font-semibold mb-3 block">
              {t('allowedPermissionsLabel')}
            </Label>
            <p className="text-xs text-muted-foreground mb-3">
              {t('allowedPermissionsDesc')}
            </p>
            {permissionGroups.length > 0 ? (
              <PermissionCheckboxes
                groups={permissionGroups}
                selected={createPermissions}
                onChange={setCreatePermissions}
              />
            ) : (
              <p className="text-xs text-muted-foreground text-center py-4">
                {t('loadingPermissions')}
              </p>
            )}
          </div>
        </div>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog
        visible={editOpen && !!selectedTenant}
        onHide={() => setEditOpen(false)}
        header={t('editTenantHeader')}
        footer={
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setEditOpen(false)}>
              {t('cancel')}
            </Button>
            <Button
              className="flex-1"
              disabled={editMutation.isPending || !editForm.name.trim()}
              onClick={() => editMutation.mutate()}
            >
              {editMutation.isPending && (
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
              )}
              {t('saveChanges')}
            </Button>
          </div>
        }
        style={{ width: '32rem', maxWidth: '100%' }}
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4">
          <div>
            <Label>{t('tenantNameLabel')}</Label>
            <Input
              value={editForm.name}
              onChange={(e) =>
                setEditForm({ ...editForm, name: e.target.value })
              }
              placeholder="Business name"
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="editIsActive"
              checked={editForm.isActive}
              onChange={(e) =>
                setEditForm({ ...editForm, isActive: e.target.checked })
              }
            />
            <Label htmlFor="editIsActive" className="cursor-pointer">
              {t('activeLabel')}
            </Label>
          </div>
        </div>
      </Dialog>

      {/* Edit Permissions Dialog */}
      <Dialog
        visible={permissionsOpen && !!selectedTenant}
        onHide={() => setPermissionsOpen(false)}
        header={selectedTenant ? t('managePermissionsFor', { name: selectedTenant.name }) : t('managePermissionsHeader')}
        footer={
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setPermissionsOpen(false)}>
              {t('cancel')}
            </Button>
            <Button
              className="flex-1"
              disabled={permissionsMutation.isPending}
              onClick={() => permissionsMutation.mutate()}
            >
              {permissionsMutation.isPending && (
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
              )}
              {t('savePermissions')}
            </Button>
          </div>
        }
        style={{ width: '42rem', maxWidth: '100%' }}
        draggable={false}
        resizable={false}
      >
        {permissionGroups.length > 0 ? (
          <PermissionCheckboxes
            groups={permissionGroups}
            selected={editPermissions}
            onChange={setEditPermissions}
          />
        ) : (
          <p className="text-xs text-muted-foreground text-center py-4">
            {t('loadingPermissions')}
          </p>
        )}
      </Dialog>

      {/* Toggle Active Confirm */}
      <ConfirmDialog
        open={toggleConfirm.open}
        title={
          toggleConfirm.tenant?.isActive ? t('deactivateTenant') : t('reactivateTenant')
        }
        message={
          toggleConfirm.tenant?.isActive
            ? t('deactivateTenantMsg', { name: toggleConfirm.tenant?.name })
            : t('reactivateTenantMsg', { name: toggleConfirm.tenant?.name })
        }
        confirmLabel={
          toggleConfirm.tenant?.isActive ? t('deactivateLabel') : t('reactivateLabel')
        }
        variant={toggleConfirm.tenant?.isActive ? 'danger' : 'warning'}
        loading={toggleMutation.isPending}
        onConfirm={() => {
          if (toggleConfirm.tenant) {
            toggleMutation.mutate(toggleConfirm.tenant, {
              onSettled: () =>
                setToggleConfirm({ open: false, tenant: null }),
            });
          }
        }}
        onCancel={() => setToggleConfirm({ open: false, tenant: null })}
      />

      {/* Archive / Unarchive Confirm */}
      <ConfirmDialog
        open={archiveConfirm.open}
        title={
          archiveConfirm.tenant?.archivedAt
            ? t('unarchiveTenant', 'Restore tenant')
            : t('archiveTenant', 'Archive tenant')
        }
        message={
          archiveConfirm.tenant?.archivedAt
            ? t('unarchiveTenantMsg', { name: archiveConfirm.tenant?.name, defaultValue: `Restore "${archiveConfirm.tenant?.name}"? The tenant will appear in listings again and logins will be re-enabled.` })
            : t('archiveTenantMsg', { name: archiveConfirm.tenant?.name, defaultValue: `Archive "${archiveConfirm.tenant?.name}"? It will be hidden from listings and logins disabled, but all data is preserved and can be restored later.` })
        }
        confirmLabel={
          archiveConfirm.tenant?.archivedAt
            ? t('unarchiveLabel', 'Restore')
            : t('archiveLabel', 'Archive')
        }
        variant={archiveConfirm.tenant?.archivedAt ? 'warning' : 'danger'}
        loading={archiveMutation.isPending}
        onConfirm={() => {
          if (archiveConfirm.tenant) {
            archiveMutation.mutate({ tenant: archiveConfirm.tenant });
          }
        }}
        onCancel={() => setArchiveConfirm({ open: false, tenant: null })}
      />

      {/* DB Status Dialog */}
      <Dialog
        visible={dbStatusOpen}
        onHide={() => { setDbStatusOpen(false); }}
        header={
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-info-text" />
            <span>{t('dbMgmtHeader', { name: selectedTenant?.name })}</span>
          </div>
        }
        footer={
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setDbStatusOpen(false)}>{t('close')}</Button>
            {dbStatus?.dbStatus !== 'DEDICATED' && dbStatus?.dbStatus !== 'MIGRATING' && (
              <Button
                className="bg-gradient-to-r from-info to-primary/80 text-info-foreground"
                onClick={() => { setDbStatusOpen(false); setMigrateConfirmOpen(true); }}
              >
                <ArrowRightLeft className="me-2 h-4 w-4" />
                {t('migrateToDedicatedBtn')}
              </Button>
            )}
          </div>
        }
        style={{ width: '36rem', maxWidth: '100%' }}
        draggable={false}
        resizable={false}
      >
        {!dbStatus ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs mb-1">{t('currentStatusLabel')}</p>
                <DbStatusBadge status={dbStatus.dbStatus} />
              </div>
              {dbStatus.migratedAt && (
                <div>
                  <p className="text-muted-foreground text-xs mb-1">{t('migratedAtLabel')}</p>
                  <p className="font-mono text-xs">{new Date(dbStatus.migratedAt).toLocaleString()}</p>
                </div>
              )}
              {dbStatus.hasDedicatedDb && (
                <div className="col-span-2">
                  <p className="text-muted-foreground text-xs mb-1">Database</p>
                  <p className="font-mono text-xs text-success-text">{t('dedicatedDbConfigured')}</p>
                </div>
              )}
            </div>

            {dbStatus.migration && (
              <div className="rounded-lg border border-border/50 bg-accent/30 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">{t('migrationJobLabel')}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    dbStatus.migration.status === 'DONE'
                      ? 'bg-success/10 text-success-text'
                      : dbStatus.migration.status === 'FAILED'
                      ? 'bg-destructive/10 text-destructive-text'
                      : 'bg-warning/10 text-warning-text'
                  }`}>
                    {dbStatus.migration.status}
                  </span>
                </div>
                {dbStatus.migration.phase && (
                  <p className="text-xs text-muted-foreground">{dbStatus.migration.phase}</p>
                )}
                <div className="relative h-2 w-full bg-muted rounded-full overflow-hidden">
                  <div
                    className="absolute start-0 top-0 h-full bg-gradient-to-r rtl:bg-gradient-to-l from-info to-primary/80 transition-all duration-500"
                    style={{ width: `${dbStatus.migration.progress}%` }}
                  />
                </div>
                <p className="text-xs text-end text-muted-foreground">{dbStatus.migration.progress}%</p>
                {dbStatus.migration.errorMsg && (
                  <p className="text-xs text-destructive-text bg-destructive/10 rounded p-2">
                    Error: {dbStatus.migration.errorMsg}
                  </p>
                )}
                {(dbStatus.migration.status !== 'DONE' && dbStatus.migration.status !== 'FAILED') && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <RefreshCw className="h-3 w-3 animate-spin" />
                    {t('autoRefreshingMsg')}
                  </div>
                )}
              </div>
            )}

            {dbStatus.dbStatus === 'SHARED' && !dbStatus.migration && (
              <div className="space-y-3">
                <div className="rounded-lg border border-info/30 bg-info/10 p-4 text-sm space-y-2">
                  <p className="font-medium text-info-text">{t('moveToDedicatedTitle')}</p>
                  <p className="text-xs text-info-text">
                    {t('moveToDedicatedDesc')}
                  </p>
                </div>

                {/* Custom server toggle */}
                <div className="rounded-lg border p-3 space-y-3">
                  <Label className="flex items-center gap-2.5 cursor-pointer font-normal leading-normal text-foreground">
                    <Checkbox
                      checked={customServerEnabled}
                      onChange={(e) => setCustomServerEnabled(e.target.checked)}
                    />
                    <span className="text-sm font-medium">{t('useCustomServer')}</span>
                  </Label>

                  {customServerEnabled && (
                    <div className="space-y-2.5 pt-1">
                      <p className="text-xs text-muted-foreground">
                        {t('customServerDesc')}
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="col-span-2">
                          <Label className="text-xs font-normal text-muted-foreground">{t('hostRequired')}</Label>
                          <Input
                            className="mt-0.5"
                            value={customServer.host}
                            onChange={(e) => setCustomServer((s) => ({ ...s, host: e.target.value }))}
                            placeholder="db.example.com"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-normal text-muted-foreground">{t('portLabel')}</Label>
                          <Input
                            className="mt-0.5"
                            value={customServer.port}
                            onChange={(e) => setCustomServer((s) => ({ ...s, port: e.target.value }))}
                            placeholder="5432"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs font-normal text-muted-foreground">{t('usernameRequired')}</Label>
                          <Input
                            className="mt-0.5"
                            value={customServer.user}
                            onChange={(e) => setCustomServer((s) => ({ ...s, user: e.target.value }))}
                            placeholder="postgres"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-normal text-muted-foreground">{t('passwordLabel')}</Label>
                          <Input
                            type="password"
                            className="mt-0.5"
                            value={customServer.password}
                            onChange={(e) => setCustomServer((s) => ({ ...s, password: e.target.value }))}
                            placeholder="••••••••"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs font-normal text-muted-foreground">{t('dbPrefixOptional')}</Label>
                          <Input
                            className="mt-0.5 font-mono"
                            value={customServer.dbPrefix}
                            onChange={(e) => setCustomServer((s) => ({ ...s, dbPrefix: e.target.value }))}
                            placeholder="pos_tenant_"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-normal text-muted-foreground">{t('dbNameOptional')}</Label>
                          <Input
                            className="mt-0.5 font-mono"
                            value={customServer.dbName}
                            onChange={(e) => setCustomServer((s) => ({ ...s, dbName: e.target.value }))}
                            placeholder="auto-generated"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {dbStatus.dbStatus === 'DEDICATED' && (
              <div className="rounded-lg border border-success/30 bg-success/10 p-4 text-sm">
                <p className="font-medium text-success-text flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  {t('tenantHasDedicatedDb')}
                </p>
                <p className="text-xs text-success-text mt-1">
                  {t('dedicatedDbAllRoutedMsg')}
                </p>
              </div>
            )}
          </div>
        )}
      </Dialog>

      {/* Subscription Dialog */}
      {subscriptionOpen && selectedTenant && (
        <SubscriptionDialog
          tenant={selectedTenant}
          onClose={() => { setSubscriptionOpen(false); setSelectedTenant(null); }}
        />
      )}

      {/* Migrate Confirm Dialog */}
      <ConfirmDialog
        open={migrateConfirmOpen}
        title={t('migrateToDedicatedTitle')}
        message={t('migrateToDedicatedMsg', { name: selectedTenant?.name })}
        confirmLabel={migrateMutation.isPending ? t('startingLabel') : t('startMigrationBtn')}
        variant="warning"
        loading={migrateMutation.isPending}
        onConfirm={() => migrateMutation.mutate()}
        onCancel={() => setMigrateConfirmOpen(false)}
      />
    </div>
  );
}
